import {
    materialLevelsColors,
    materialLevelsIcons,
    materialLevelsNames,
} from "@common/chapters/ActivityLevelBadge"
import {
    getDefaultMaterialDuration,
    MATERIAL_DURATION_OPTIONS,
} from "@common/chapters/materialDuration"
import { buildRegeneratePayload } from "../study-missions/buildRegeneratePayload"
import { isGenerationFormValid } from "./generationFormValidity"
import { useClassDefaultDuration } from "./useClassDefaultDuration"
import AvatarM from "@common/components/avatar/AvatarM"
import FilledButtonM from "@common/components/button/FilledButtonM"
import IconButtonM from "@common/components/button/IconButtonM"
import TouchableRippleM from "@common/components/button/TouchableRippleM"
import { IconImageM } from "@common/components/icons/IconsM"
import { ModalM } from "@common/components/modal/ModalM"
import { SnackBarM } from "@common/components/alert/snackbar/SnackBar"
import TextM from "@common/components/text/TextM"
import useLiveKitEventSender from "@common/hooks/livekit/useLiveKitEventSender"
import { useApiStatus } from "@common/hooks/useApiStatus"
import { useRequestsBatch } from "@common/hooks/useRequestsBatch"
import { theme } from "@common/theme/theme"
import { DimensionsContext, IS_WEB } from "@common/utils/mobileUtils"
import { subjectWithPartitive } from "@common/utils/subjectPartitive"
import { sectionStudentClasses, studentClasses } from "@data/constants/formConstants"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { activitiesService } from "@data/services/activitiesService"
import { chaptersService } from "@data/services/chaptersService"
import { materialsService } from "@data/services/materialsService"
import { schoolAssessmentsService } from "@data/services/schoolAssessmentsService"
import { studentsService } from "@data/services/studentsService"
import { findItemInListWithId } from "@data/utility/dataUtils"
import { createColorVariation, hasFeature } from "@data/utility/Utils"
import { FontAwesome5 } from "@expo/vector-icons"
import { useNavigation } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"
import axios from "axios"
import { LinearGradient } from "expo-linear-gradient"
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { ScrollView, TouchableOpacity, View } from "react-native"
import { ActivityIndicator } from "react-native-paper"
import { connect } from "react-redux"
import ChapterSelectionModal from "../../common/chapters/ChapterSelectionModal"
import StudyProgramChapterSelectionModal from "../../common/chapters/StudyProgramChapterSelectionModal"
import { ChampionImageM } from "../../student/champions/ChampionImageM"
import { SelectInputM } from "../../student/account/onboarding/SelectInputM"
import SearchableSelectM from "../../common/components/input/SearchableSelectM"
import UncontrolledTextInputM from "../../common/components/input/UncontrolledTextInputM"
import CreateActivityButton from "./components/CreateActivityButton"
import ImageCropModal from "./components/ImageCropModal"
import ImageUploadZone from "./components/ImageUploadZone"
import MaterialGenerationForm from "./components/MaterialGenerationForm"
import MaterialResultsCards from "./components/MaterialResultsCards"
import SkillPartsChipPicker from "./components/SkillPartsChipPicker"
import { isPdfFile } from "@common/components/pdf/pdfHelpers"

// Fallback matière when we can't infer one from the student (no past activity,
// or multi-student mode). Maths is the most generated subject by a wide margin.
const DEFAULT_SCHOOL_SUBJECT_ID = "1"

// La même modale est ouverte depuis l'espace parent (bouton « + » de la
// todo-list) : l'élève et le tuteur gardent le tutoiement, le parent est
// vouvoyé et on parle de « l'activité », pas de « ton activité ».
const TEXTS = {
    student: {
        suggestionUnavailable: "Suggestion AI indisponible. Choisis le chapitre manuellement.",
        pickChapter: " — choisis un chapitre.",
        composing: "Sam compose ton activité…",
        customInstructions: "Décris ce qu'on veut générer (par ex. concept à revoir, méthode à entraîner)…",
        programChapterMissing: "Choisis un chapitre du programme",
    },
    parent: {
        suggestionUnavailable: "Suggestion AI indisponible. Choisissez le chapitre manuellement.",
        pickChapter: " — choisissez un chapitre.",
        composing: "Sam compose l'activité…",
        customInstructions: "Décrivez ce qu'on veut générer (par ex. concept à revoir, méthode à entraîner)…",
        programChapterMissing: "Choisissez un chapitre du programme",
    },
}

const GenerateActivityModal = (props) => {
    const {
        isOpen,
        onClose,
        student,
        schoolSubjects,
        mode = "standard",
        selectedLevel = null, // '1' | '2' | '3' - difficulty for similar mode (replaces parcoursType)
        selectedDuration = null, // '5' | '10' | '15' - duration in minutes for similar mode
        initialStep = "form",
        materialId = null,
        nav = null,
        initialImage = null, // New: pre-loaded image to start analysis immediately
        forStudents = false, // New: enable multi-student selection mode
        studentsList = [], // New: list of students for multi-select
        initialStudentIds = [], // New: pre-selected student IDs
        selfCreate = false, // Student creates an activity for self — bypasses tutor-only endpoints
        studentAssessments = [], // From Redux - student's upcoming assessments
        // Similar mode customization
        similarInstructions = null,
        similarStudentInterests = null,
        // When provided, the primary "Générer une activité" button calls
        // this callback with the form values instead of running the
        // built-in 3-cards generation flow. Used by Sam Prescription so
        // the regen lands as a single activity on the existing draft
        // (via studyMissionsService.regenerate) rather than creating
        // standalone Activities through MaterialResultsCards.
        onSubmitOverride = null,
        // Pre-select a chapter on mount (the chapter Sam auto-detected
        // for the live's lead activity). Accepts the chapter id as a
        // string — same shape the form's selectedChapter field expects.
        initialChapterId = null,
    } = props

    // Modale embarquée comme simple formulaire de cadrage (Sam Prescription) :
    // l'appelant fournit `onSubmitOverride`, donc pas d'étape « 3 cartes », et
    // ni la zone de dépôt OCR ni le renvoi vers l'ancienne interface n'ont de
    // sens dans ce contexte. Déduit plutôt que passé en deux booléens de plus :
    // ils disaient la même chose, et un appelant pouvait en oublier un.
    const embeddedAsForm = !!onSubmitOverride

    // Check if feature flags are enabled
    // Use currentLiveSession if in live mode, otherwise fall back to first live session
    const liveRoomToCheck = props.currentLiveSession?.live_room
        || props.currentUser?.live_sessions?.[0]?.live_room
    const isPrerequisiteQuizEnabled = hasFeature(liveRoomToCheck, "prerequisite-quiz")
    const [step, setStep] = useState(initialStep) // "form" | "results"
    const [chaptersOptions, setChaptersOptions] = useState([])
    const [chapters, setChapters] = useState([]) // Full chapter objects
    const [materials, setMaterials] = useState([])
    // La dernière demande d'énoncés du formulaire : « Autres énoncés » et le ↻
    // d'une carte la rejouent en sautant la réserve.
    const lastGenerationRequestRef = useRef(null)
    // La fournée des cartes affichées : remplacer une carte la garde, pour que le
    // format tiré pour cette carte reste distinct de celui des deux autres.
    const lastBatchIdRef = useRef(null)
    const [loadingStates, setLoadingStates] = useState({}) // Track loading state per material
    const [quizErrorStates, setQuizErrorStates] = useState({})
    const [pendingQuizMaterials, setPendingQuizMaterials] = useState({})

    // Image flow quiz state
    const [imageFlowQuizEnabled, setImageFlowQuizEnabled] = useState(false)
    const [imageFlowQuizError, setImageFlowQuizError] = useState(false)

    // Multi-student selection state
    const [selectedStudentIds, setSelectedStudentIds] = useState(initialStudentIds?.length > 0 ? initialStudentIds : (forStudents && studentsList?.length > 0 ? studentsList.map(s => s.id) : []))
    const [showStudentSelection, setShowStudentSelection] = useState(forStudents && initialStudentIds?.length === 0)
    // Quand l'appelant fournit deja le chapitre (Sam Prescription : celui que
    // l'eleve vient de travailler pendant le live), c'est lui le cadrage, et le
    // formulaire s'ouvre en mode manuel dessus.
    //
    // Ouvrir sur le programme de revision presenterait un selecteur VIDE : le
    // pre-remplissage alimente `selectedChapter`, que ce mode ne lit pas. Et
    // comme sa validation exige un chapitre du programme, le bouton reste
    // desactive — le tuteur ne peut plus regenerer sans aller choisir un
    // chapitre qu'il n'a pas demande. L'interrupteur reste la pour y basculer.
    const [useStudyProgram, setUseStudyProgram] = useState(
        embeddedAsForm ? false : student?.has_study_program
    )
    const [showChapterSelection, setShowChapterSelection] = useState(false)

    // In multi-student mode, pick the first selected student as representative for AI analysis calls
    const resolvedStudent = forStudents
        ? studentsList?.find(s => selectedStudentIds?.includes(s?.id))
        : student

    // Élèves destinataires pour l'historique anti-répétition backend :
    // en multi-élèves l'exercice sera servi à tous → union des historiques.
    const historyStudentIds = forStudents && selectedStudentIds?.length > 0
        ? selectedStudentIds
        : student?.id ? [student.id] : []

    // Image-based generation state
    const [imageMode, setImageMode] = useState(false)
    const [uploadedImage, setUploadedImage] = useState(null)
    const [materialFromImage, setMaterialFromImage] = useState(null)
    const [activityTitle, setActivityTitle] = useState("")

    // Ref to track the last image object that was submitted for upload.
    // Prevents re-uploading the same image when imageUploadSubmitted resets.
    const lastStartedUploadRef = useRef(null)

    // Ref to track which analysis we already auto-pre-selected the chapter
    // for. Without this, the auto-select effect (which depends on
    // watchedChapter) re-fires every time watchedChapter flips back to null,
    // including when the tutor explicitly clears the chip with ✕ — that would
    // re-set the chip on the very next render. The ref is reset when the
    // image is removed, when a new analysis starts, when the modal closes,
    // and when the tutor changes class/subject (so a fresh analysis under a
    // new scope can apply).
    const autoSelectedAnalysisKeyRef = useRef(null)

    // Ref to the form's outer ScrollView. When the tutor clicks the disabled
    // drop zone we scroll back to the class+subject dropdowns so they can fill
    // the missing context — feels more natural than a toast.
    const formScrollViewRef = useRef(null)

    const handleDropZoneActivateWhenDisabled = useCallback(() => {
        if (formScrollViewRef.current?.scrollTo) {
            formScrollViewRef.current.scrollTo({ y: 0, animated: true })
        }
    }, [])

    useEffect(() => {
        if (imageMode && materialFromImage?.title) {
            setActivityTitle(materialFromImage.title)
        }
    }, [materialFromImage?.title, imageMode])

    // Web-only: Image crop modal state
    const [imageToCrop, setImageToCrop] = useState(null)
    const [showCropModal, setShowCropModal] = useState(false)

    // v3 single-call pipeline state — non-blocking enhancement on top of the
    // manual MaterialGenerationForm flow. The form (chapter dropdown,
    // class/subject) stays interactive while analysis runs in the background.
    //
    //   idle      → no image, or image analysis not started
    //   analyzing → upload + OCR + LLM running (small spinner under chapter)
    //   done      → analysisSuggestion is populated (or null if low-confidence + no chapter)
    //   error     → soft-fail; the manual form path is still fully usable
    const [analysisStatus, setAnalysisStatus] = useState('idle')
    // Shape: { suggestion: {id, name, parent_name} | null,
    //          alternatives: [{id, name, parent_name}],
    //          confidence: 'high'|'medium'|'low',
    //          level, title, chapter_skills, material_skills }
    const [analysisSuggestion, setAnalysisSuggestion] = useState(null)

    // Scope-mismatch suggestion: analyze-material now runs a
    // second, independent detection (volet 2) of the real subject/class of the
    // content, regardless of what the tutor picked. When it's confident and
    // differs from the tutor's choice, we surface a "Basculer" chip — no
    // chapter proposed here, just the scope switch. Accepting it re-runs the
    // chapter search in the new scope via reanalyzeScope() (no new OCR).
    // Derived (useMemo, below) from analysisSuggestion + the tutor's current
    // selection — shape: { school_subject_id, school_subject_name, class }.
    const [crossClassDismissed, setCrossClassDismissed] = useState(false)
    const [crossClassLoading, setCrossClassLoading] = useState(false)

    // Holds the anchor PATCH in flight as `{ materialId, chapter }`, so the
    // success handler knows what was confirmed. "Is one in flight" for
    // rendering comes from redux (`props.updateChapterRequest`), which clears
    // on every terminal state — a local mirror would stay stuck when the
    // failure carries no message and `useApiStatus` skips our callback.
    const chapterAnchorRef = useRef(null)
    // The chapter whose anchor failed, not a bare "it failed" flag: the banner
    // is then only shown while that same chapter is still the pinned one, and
    // pinning another is what re-arms the effect. A boolean needed manual
    // resets on every path, and still left the banner up when the tutor pinned
    // the chapter the analyse had already applied.
    const [failedAnchorChapterId, setFailedAnchorChapterId] = useState(null)

    const [creatingActivity, setCreatingActivity] = useState(false)
    const [analysisError, setAnalysisError] = useState(null)
    const [activityCreated, setActivityCreated] = useState(false)
    const [showImageChapterSearch, setShowImageChapterSearch] = useState(false)
    const [personalizeWithInterests, setPersonalizeWithInterests] = useState(false)

    // Image-mode "Personnaliser…" inline expand. The 2 quick parcours buttons
    // ("Pour réviser" / "Pour s'entraîner") fire generation immediately with
    // hardcoded levels. The 3rd "Personnaliser…" button toggles an inline
    // expand that surfaces Type, Instructions, and the personalize-with-interests
    // toggle so the tutor can fine-tune before generating.
    const [showCustomParcours, setShowCustomParcours] = useState(false)
    const [customLevel, setCustomLevel] = useState("2")
    const [customInstructions, setCustomInstructions] = useState("")

    // Tutor-controlled skill_part subset for activity generation.
    // Reset when the chapter changes; defaults to first 3 positions when the
    // chapter's skill_parts arrive (or all if 1-2 are seeded). Empty array =
    // section not yet initialized for the active chapter.
    const [selectedSkillPartPositions, setSelectedSkillPartPositions] = useState([])

    const dimensions = useContext(DimensionsContext)
    const isSmallScreen = dimensions?.width < 800

    const texts = TEXTS[props.isParentMode ? "parent" : "student"]

    const { sendLiveKitData: sendNewTodo } = useLiveKitEventSender()

    // Memoize last activity to avoid re-sorting on every render
    const lastActivity = useMemo(() => {
        if (!student?.activities?.length) return null
        return [...student.activities].sort(
            (a, b) => new Date(b.finished_at) - new Date(a.finished_at)
        )[0]
    }, [student?.activities])

    const lastActivitySubjectId = lastActivity?.school_subject_id

    // The scope the manual flow starts from: the student's own class, and the
    // subject of their last activity (Maths as the catch-all when they have no
    // history — or in multi-student mode, where there is no single `student`).
    // Used both to seed the form and to hand the scope back when the tutor
    // leaves study-program mode, so the two can't drift apart.
    const defaultScope = useMemo(
        () => ({
            class: student?.profile?.class || "",
            subject: lastActivitySubjectId
                ? lastActivitySubjectId.toString()
                : DEFAULT_SCHOOL_SUBJECT_ID,
        }),
        [student?.profile?.class, lastActivitySubjectId]
    )

    const {
        control,
        watch,
        handleSubmit,
        setValue,
        getValues,
        // Déstructuré ici, pendant le rendu : `formState` est un Proxy qui
        // ne suit que les champs lus à ce moment. Le consulter uniquement
        // depuis onSubmit renverrait un objet vide.
        formState: { dirtyFields },
    } = useForm({
        defaultValues: {
            selectedClass: defaultScope.class,
            selectedSubject: defaultScope.subject,
            instructions: "",
            selectedChapter: initialChapterId
                ? initialChapterId.toString()
                : null,
            selectedLevel: "2",
            selectedDuration: getDefaultMaterialDuration(student?.profile?.class),
            studyProgramChapter: null,
        },
    })

    const watchedClass = watch("selectedClass")
    const watchedSubject = watch("selectedSubject")
    const watchedInstructions = watch("instructions")
    const watchedChapter = watch("selectedChapter")
    const watchedStudyProgramChapter = watch("studyProgramChapter")

    const { onDurationPicked, forgetPickedDuration } = useClassDefaultDuration({
        watchedClass,
        setValue,
    })

    // Class-scoped subjects override. The Redux `schoolSubjects` prop holds the
    // global catalogue (used by RegisterScreen and others). For activity
    // generation we only want subjects that have at least one validated
    // non-legacy chapter for the selected class — so we re-fetch a scoped list
    // from `GET /api/school-subjects?class=X` and use it locally.
    //
    // Falls back to the global Redux list when:
    //   - no class is selected yet
    //   - the request errored (silent fallback, no toast)
    const [scopedSubjects, setScopedSubjects] = useState(null)

    useEffect(() => {
        let cancelled = false
        if (!watchedClass) {
            setScopedSubjects(null)
            return
        }
        // Drop the previous class's list before fetching the new one. Keeping it
        // would make `effectiveSchoolSubjects` describe the *old* class for the
        // duration of the request, and the "subject no longer available" effect
        // below would then reset a perfectly valid subject (e.g. leaving program
        // mode restores the student's subject while the list still describes the
        // program's class). Falling back to the global catalogue meanwhile is
        // permissive, never wrong.
        setScopedSubjects(null)
        axios
            .get(`/api/school-subjects`, { params: { class: watchedClass } })
            .then((res) => {
                if (cancelled) return
                const data = res?.data?.data ?? res?.data
                setScopedSubjects(Array.isArray(data) ? data : null)
            })
            .catch(() => {
                if (cancelled) return
                // Silent fallback to the global list on error.
                setScopedSubjects(null)
            })
        return () => {
            cancelled = true
        }
    }, [watchedClass])

    const effectiveSchoolSubjects = scopedSubjects ?? schoolSubjects

    // If the selected subject is no longer in the scoped list (e.g. tutor
    // switches from lycée to collège and the previously chosen subject only
    // exists at the lycée level), reset the subject + chapter selection.
    // Skipped in study-program mode: there the subject is derived from the
    // picked program chapter (see the sync effect below), not chosen by the
    // tutor, so the class-scoped catalogue has no say over it.
    useEffect(() => {
        if (useStudyProgram) return
        if (!watchedSubject || !effectiveSchoolSubjects) return
        const stillAvailable = effectiveSchoolSubjects.some(
            (s) => s.id?.toString() === watchedSubject?.toString()
        )
        if (!stillAvailable) {
            setValue("selectedSubject", null)
            setValue("selectedChapter", null)
        }
    }, [effectiveSchoolSubjects, watchedSubject, useStudyProgram])

    // Study-program mode: the program chapter IS the scope. The tutor picks a
    // chapter from the student's program, and class + matière are derived from
    // it — never asked. Letting them be picked separately made the two drift
    // apart (an Arabic chapter generated under a "Maths" matière). Both fields
    // stay in the form so every scope-dependent surface (drop zone context,
    // image analyse, subject fallbacks in the result cards) reads a coherent
    // value; they are just no longer editable inputs in this mode. No chapter
    // picked yet → empty scope, which is what keeps the drop zone locked.
    useEffect(() => {
        if (!useStudyProgram) return
        const nextClass = watchedStudyProgramChapter?.class || ""
        const nextSubject = watchedStudyProgramChapter?.school_subject_id
            ? watchedStudyProgramChapter.school_subject_id.toString()
            : ""
        if ((watchedClass || "") !== nextClass) {
            setValue("selectedClass", nextClass)
        }
        if ((watchedSubject || "") !== nextSubject) {
            setValue("selectedSubject", nextSubject)
        }
    }, [
        useStudyProgram,
        watchedStudyProgramChapter,
        watchedClass,
        watchedSubject,
        setValue,
    ])

    // Entering/leaving study-program mode. Leaving it hands the scope back to
    // the tutor, so we restore the same defaults the modal opens with in manual
    // mode — otherwise they'd land on two empty dropdowns, since program mode
    // keeps class/matière pinned to the program chapter (empty when none is
    // picked). Every path out of program mode must go through here, not through
    // setUseStudyProgram directly: the tutor-facing switch AND the automatic
    // flip below both change the mode, and only restoring on one of them is how
    // the class/matière drift this modal just got fixed for crept in.
    const handleSetUseStudyProgram = useCallback(
        (value) => {
            const next = Boolean(value)
            const modeChanges = next !== Boolean(useStudyProgram)
            setUseStudyProgram(next)
            // Only a real mode change may touch the scope. The availability
            // effect below fires on "does any selected student have a program",
            // which is not the same question as "is the mode on": deselecting
            // the program-carrying student after the tutor had already switched
            // the mode off would otherwise run the restore anyway and wipe the
            // class/matière they had just picked by hand.
            if (!modeChanges) return
            if (next) {
                // Entering: the program chapter takes over as the scope, so the
                // manual pick must go — leaving it would resurface as a stale
                // chip the next time the tutor switches back.
                setValue("selectedChapter", null)
            } else {
                setValue("studyProgramChapter", null)
                setValue("selectedClass", defaultScope.class)
                setValue("selectedSubject", defaultScope.subject)
            }
        },
        [setValue, defaultScope, useStudyProgram]
    )

    // Follow the selected students' study-program availability: the mode is only
    // offered when at least one of them has a program, so changing the selection
    // (the avatar row reopens the picker mid-form) can flip the mode on its own.
    //
    // Keyed on the *transition*, not the value: `studentsList` is often a fresh
    // array on every parent render (it's derived from the live session), so
    // re-asserting the value here would re-run the scope restore — wiping a
    // class/matière the tutor had just picked — and would also fight a manual
    // toggle by clamping the mode straight back.
    const lastProgramAvailabilityRef = useRef(null)
    useEffect(() => {
        if (!forStudents || !studentsList) return
        const hasStudyProgram = studentsList
            ?.filter((x) => selectedStudentIds?.includes(x?.id))
            ?.reduce((acc, x) => acc || x?.has_study_program, false)
        if (lastProgramAvailabilityRef.current === hasStudyProgram) return
        lastProgramAvailabilityRef.current = hasStudyProgram
        handleSetUseStudyProgram(hasStudyProgram)
    }, [
        selectedStudentIds,
        forStudents,
        studentsList,
        handleSetUseStudyProgram,
    ])

    // Level options
    const levelOptions = Object.keys(materialLevelsColors)?.map((x) => ({
        label: materialLevelsNames?.[x],
        value: x,
        leftComponent: (
            <IconImageM
                source={materialLevelsIcons[x]}
                width={18}
                style={{
                    tintColor: materialLevelsColors[x],
                    marginRight: 10,
                }}
            />
        ),
    }))

    // Class options grouped by EN cycle (Primaire / Collège / Lycée). The
    // grouping renders as section headers in SearchableSelectM. "Autre" is
    // dropped via slice(0, -1) like before.
    const classGroupOf = (cls) =>
        sectionStudentClasses.find((s) => s.options.includes(cls))?.title ?? "Autre"
    const classOptions = studentClasses
        .map((x) => ({ label: x, value: x, group: classGroupOf(x) }))
        .slice(0, -1)

    // Subject options — driven by the class-scoped list when available.
    const subjectOptions =
        effectiveSchoolSubjects?.map((subject) => ({
            label: subject.name,
            value: subject.id.toString(),
            leftComponent: (
                <IconImageM
                    uri={subject?.icon}
                    width={18}
                    style={{ marginRight: 10 }}
                />
            ),
        })) || []

    // Helper function to create material update handlers for useApiStatus
    // This reduces code duplication across multiple similar API handlers
    const createMaterialUpdateHandlers = useCallback((filterField, filterValue, transformData = null) => ({
        onSuccess: (data) => {
            const material = transformData ? transformData(data) : (data?.data || data)

            setMaterials((prev) => prev.map((m) =>
                m[filterField] === filterValue
                    ? {
                          ...m,
                          ...material,
                          // Preserve frontend-set variation/skills_part — the API response
                          // sets variation to null which would clobber our card labels.
                          variation: m.variation,
                          skills_part: m.skills_part,
                          status: "success",
                          id: material.id,
                          title: material.title,
                          statement: material.statement,
                          body: material.body,
                          text: material.text,
                          material_key_skills: material.material_key_skills,
                          school_subject: material.school_subject || findItemInListWithId(
                              material.school_subject_id || watchedSubject,
                              effectiveSchoolSubjects
                          ),
                          chapter_name: material.chapter_name ||
                                       material.chapter_model?.name ||
                                       chaptersOptions.find((c) => c.value === watchedChapter)?.label ||
                                       findItemInListWithId(watchedSubject, effectiveSchoolSubjects)?.name,
                          level: material.level,
                          estimated_duration: material.estimated_duration,
                          num_clusters: material.num_clusters || m.num_clusters,
                      }
                    : m
            ))
        },
        onError: (error) => {
            // `error` here is already `error.response.data` (see the api
            // action's `failure()` reducer). Only trust the message for the
            // one error_code the backend actually crafted for the tutor
            // (materials/generate's 502) — this shared handler also serves
            // similar-exercises/proposition endpoints whose failure bodies
            // can carry a raw exception string or a generic Laravel
            // validation message, neither fit to show as-is.
            const errorMessage = error?.error_code === "material_generation_failed"
                ? (error?.message || error?.error || null)
                : null

            setMaterials((prev) => prev.map((m) =>
                m[filterField] === filterValue ? { ...m, status: "error", error_message: errorMessage } : m
            ))
        }
    }), [watchedSubject, watchedChapter, effectiveSchoolSubjects, chaptersOptions])

    // Cross-class chapter pick: holds the id we want to apply once the
    // refetched chapters list arrives in the next getChaptersAsTutor success.
    // A ref (not state) is used on purpose so we don't trigger an extra render.
    //
    // Initialized from `initialChapterId` (Sam Prescription embedding) so the
    // first chapters fetch on mount re-applies the auto-detected chapter that
    // the [watchedClass, watchedSubject] reset effect would otherwise wipe.
    const pendingChapterIdRef = useRef(
        initialChapterId ? initialChapterId.toString() : null
    )

    // When the tutor picks a skill_part row in the search dropdown, we want
    // the matching chip in SkillPartsChipPicker to be pre-selected once the
    // chapter's skill_parts have loaded. Stored as { chapterId, position }
    // so we can verify it's still relevant when the chip set arrives.
    const pendingSkillPartPositionRef = useRef(null)

    // Handle chapters API request success
    useApiStatus(
        chaptersService.getChaptersAsTutor,
        null,
        true,
        (data) => {
            if (data) {
                const chaptersData = data?.chapters || []
                // Store full chapters for ChapterSearchDropdown
                setChapters(chaptersData)
                // Keep options for backward compatibility
                const options = chaptersData.map((chapter) => ({
                    label: chapter.name,
                    value: chapter.id.toString(),
                }))
                setChaptersOptions(options)

                // Apply a pending cross-class pick (see
                // handleGlobalChapterSelect): the class/subject change we
                // triggered above resets selectedChapter to null; once the
                // chapters arrive we restore the picked id.
                if (pendingChapterIdRef.current) {
                    const idToApply = pendingChapterIdRef.current
                    pendingChapterIdRef.current = null
                    setValue("selectedChapter", idToApply.toString())
                }
            } else {
                setChapters([])
                setChaptersOptions([])
            }
        },
        () => {
            // Drop any pending cross-class pick on error so it doesn't fire
            // against an unrelated subsequent fetch.
            pendingChapterIdRef.current = null
        }
    )

    // Fetch student's upcoming assessments when modal opens (via Redux)
    useEffect(() => {
        if (isOpen && student?.id && !forStudents) {
            props.apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                { student_id: student.id, upcoming: true }
            )
        }
    }, [isOpen, student?.id, forStudents])

    // Compute priority chapters from next assessment for selected subject
    const { priorityChapterIds, priorityBadge } = useMemo(() => {
        if (!watchedSubject || studentAssessments.length === 0) {
            return { priorityChapterIds: [], priorityBadge: null }
        }

        // Find the next assessment for the selected subject
        const nextAssessmentForSubject = studentAssessments.find(
            (assessment) =>
                assessment.school_subject?.id?.toString() === watchedSubject?.toString()
        )

        if (!nextAssessmentForSubject) {
            return { priorityChapterIds: [], priorityBadge: null }
        }

        // Get chapter IDs from the assessment
        const chapterIds = nextAssessmentForSubject.chapters?.map((c) =>
            c.id?.toString()
        ) || []

        // Build badge (J-X)
        const daysUntil = nextAssessmentForSubject.days_until
        const badge = daysUntil === 0 ? "Aujourd'hui" : `J-${daysUntil}`

        return { priorityChapterIds: chapterIds, priorityBadge: badge }
    }, [watchedSubject, studentAssessments])

    // Currently selected subject's display name — used in the chapter
    // dropdown's "Aucun résultat dans <classe> · <matière>" CTA copy.
    const currentSubjectName = useMemo(() => {
        if (!watchedSubject) return null
        return (
            effectiveSchoolSubjects?.find(
                (s) => s.id?.toString() === watchedSubject?.toString()
            )?.name || null
        )
    }, [watchedSubject, effectiveSchoolSubjects])

    // Handler fired when the tutor picks a chapter from the cross-class
    // global search fallback. We need to:
    //   1. Sync class + subject to the picked chapter's scope
    //   2. Wait for the chapters refetch to settle (the existing useEffect on
    //      [watchedClass, watchedSubject] will reset selectedChapter to null
    //      and trigger getChaptersAsTutor; the success handler then applies
    //      pendingChapterIdRef)
    //   3. If the scope already matches, skip the dance and set the chapter
    //      directly.
    const handleGlobalChapterSelect = useCallback(
        (result) => {
            if (!result?.chapter_id) return

            const targetClass = result.class
            const targetSubjectId = result.school_subject_id?.toString()
            const chapterIdString = result.chapter_id.toString()
            const targetSkillPartPosition =
                typeof result.skill_part_position === "number"
                    ? result.skill_part_position
                    : null

            // Queue the skill_part position the user picked, so the
            // SkillPartsChipPicker auto-selects the matching chip once the
            // chapter's skill_parts arrive (the chapter-change effect resets
            // selectedSkillPartPositions, which would otherwise wipe a direct
            // setSelectedSkillPartPositions call here).
            if (targetSkillPartPosition !== null) {
                pendingSkillPartPositionRef.current = {
                    chapterId: result.chapter_id,
                    position: targetSkillPartPosition,
                }
            }

            const classMatches = targetClass && watchedClass === targetClass
            const subjectMatches =
                targetSubjectId && watchedSubject?.toString() === targetSubjectId

            if (classMatches && subjectMatches) {
                // Same scope — set the chapter directly. The chapter may not
                // be in the in-memory list (it came from the global search),
                // but the dropdown stores selection by id either way.
                setValue("selectedChapter", chapterIdString)
                return
            }

            // Different scope: queue the chapter id for the next chapters
            // success, then sync class + subject. The order matters — set the
            // pending ref BEFORE updating class/subject so the refetch's
            // success callback finds it.
            pendingChapterIdRef.current = chapterIdString
            if (targetClass && !classMatches) {
                setValue("selectedClass", targetClass)
            }
            if (targetSubjectId && !subjectMatches) {
                setValue("selectedSubject", targetSubjectId)
            }

            SnackBarM.show({
                type: "success",
                text1: "Classe et matière mises à jour pour ce chapitre.",
            })
        },
        [watchedClass, watchedSubject, setValue]
    )

    // Fetch chapters when class or subject changes
    useEffect(() => {
        // In multi-student mode, we need selected students to fetch chapters
        const hasRequiredStudentContext = forStudents
            ? selectedStudentIds?.length > 0
            : true // Single student mode doesn't need this check

        if (watchedClass && watchedSubject && hasRequiredStudentContext) {
            // Reset selected chapter when subject changes. Au montage, le
            // chapitre pre-rempli par l'appelant est efface ici puis
            // RE-APPLIQUE par `pendingChapterIdRef` quand la liste arrive —
            // c'est le role que ce ref porte deja.
            setValue("selectedChapter", null)

            const params = {
                school_subject_id: watchedSubject,
                class: watchedClass,
                // Backend caps page_size at 100. Default 20 was hiding chapters in
                // long curricula (e.g., 86 in 1ère/Français). 100 covers every
                // observed (class, subject) combo today; if a future combo
                // exceeds it we'll need real pagination here.
                page_size: 100,
            }

            // For multi-student mode, pass student_ids in params
            if (forStudents && selectedStudentIds?.length > 0) {
                params.student_ids = selectedStudentIds.join(',')
            }

            props.apiRequest(
                chaptersService.getChaptersAsTutor,
                { id: student?.id },
                {},
                params
            )
        } else {
            setChapters([])
            setChaptersOptions([])
        }
    }, [watchedClass, watchedSubject, student?.id, forStudents, selectedStudentIds])

    // Active chapter id for the skill_parts picker. Both the standard and
    // study-program flows feed into chapter_id at submit time, so we react to
    // either being selected.
    const activeChapterId = useMemo(() => {
        if (useStudyProgram) {
            return watchedStudyProgramChapter?.id || null
        }
        return watchedChapter ? parseInt(watchedChapter, 10) : null
    }, [useStudyProgram, watchedStudyProgramChapter, watchedChapter])

    // Fetch the chapter's skill_parts when the chapter changes (cached in
    // Redux by chapter_id, so revisits don't re-fetch). The cache is treated
    // as a hit only when it carries skill_parts: an empty array (legitimate
    // "no skill_parts at the time of fetch" or stale persisted state from
    // before the chapter was backfilled) must trigger a refetch — otherwise
    // tutors keep seeing an empty picker even after the curriculum import
    // adds skill_parts to that chapter, until they log out.
    useEffect(() => {
        // Reset the local selection whenever the chapter changes; the next
        // useEffect fills it once the skill_parts arrive in Redux.
        setSelectedSkillPartPositions([])
        if (!activeChapterId) {
            return
        }
        // Self-create flow doesn't expose the skill_parts picker, and the
        // route is gated by manage_student_chapters which students don't have.
        if (selfCreate) {
            return
        }
        const cached = props.skillPartsByChapterId?.[activeChapterId]
        if (cached && cached.length > 0) {
            return
        }
        props.apiRequest(
            chaptersService.getChapterSkillParts,
            { chapterId: activeChapterId }
        )
    }, [activeChapterId, selfCreate])

    // Skill_parts available for the active chapter (sorted by position).
    const activeChapterSkillParts = useMemo(() => {
        if (!activeChapterId) return []
        return props.skillPartsByChapterId?.[activeChapterId] || []
    }, [activeChapterId, props.skillPartsByChapterId])

    // No default selection: the picker starts empty and the backend's random
    // sampling (3 distinct positions) handles broad coverage. The tutor
    // opt-ins by selecting chips when they want to focus on specific skills.

    // Apply a pending skill_part selection once the chip list has loaded.
    // Triggered when the tutor picked a skill_part row in the search dropdown
    // — handleGlobalChapterSelect queued { chapterId, position } in a ref so
    // the chapter-change reset can run first without wiping the intent.
    useEffect(() => {
        const pending = pendingSkillPartPositionRef.current
        if (!pending) return
        if (!activeChapterId) return
        if (Number(pending.chapterId) !== Number(activeChapterId)) return
        if (activeChapterSkillParts.length === 0) return
        const exists = activeChapterSkillParts.some(
            (sp) => sp.position === pending.position
        )
        if (!exists) {
            // The picked skill_part isn't in the current list (deleted or
            // filtered) — drop the intent rather than leaving stale state.
            pendingSkillPartPositionRef.current = null
            return
        }
        setSelectedSkillPartPositions([pending.position])
        pendingSkillPartPositionRef.current = null
    }, [activeChapterId, activeChapterSkillParts])

    const toggleSkillPartPosition = useCallback((position) => {
        setSelectedSkillPartPositions((prev) => {
            const isSelected = prev.includes(position)
            if (isSelected) {
                return prev.filter((p) => p !== position)
            }
            return [...prev, position]
        })
    }, [])

    // Handle API responses for skills_part 1, 2, 3 (factorized)
    const skillsPart1Handlers = useMemo(() => createMaterialUpdateHandlers('skills_part', 1), [createMaterialUpdateHandlers])
    const skillsPart2Handlers = useMemo(() => createMaterialUpdateHandlers('skills_part', 2), [createMaterialUpdateHandlers])
    const skillsPart3Handlers = useMemo(() => createMaterialUpdateHandlers('skills_part', 3), [createMaterialUpdateHandlers])

    useApiStatus(activitiesService.generateMaterialsSkillsPart1, null, false, skillsPart1Handlers.onSuccess, skillsPart1Handlers.onError)
    useApiStatus(activitiesService.generateMaterialsSkillsPart2, null, false, skillsPart2Handlers.onSuccess, skillsPart2Handlers.onError)
    useApiStatus(activitiesService.generateMaterialsSkillsPart3, null, false, skillsPart3Handlers.onSuccess, skillsPart3Handlers.onError)

    // Handle API responses for similar exercises (factorized)
    const moreDifficultHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'more_difficult'), [createMaterialUpdateHandlers])
    const sameDifficultyHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'same_difficulty'), [createMaterialUpdateHandlers])
    const sameDifficultyDiversifiedHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'same_difficulty_diversified'), [createMaterialUpdateHandlers])
    const otherConceptsHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'other_concepts'), [createMaterialUpdateHandlers])

    useApiStatus(activitiesService.generateSimilarExercisesMoreDifficult, null, true, moreDifficultHandlers.onSuccess, moreDifficultHandlers.onError)
    useApiStatus(activitiesService.generateSimilarExercisesSameDifficulty, null, true, sameDifficultyHandlers.onSuccess, sameDifficultyHandlers.onError)
    useApiStatus(activitiesService.generateSimilarExercisesSameDifficultyDiversified, null, true, sameDifficultyDiversifiedHandlers.onSuccess, sameDifficultyDiversifiedHandlers.onError)
    useApiStatus(activitiesService.generateSimilarExercisesOtherConcepts, null, true, otherConceptsHandlers.onSuccess, otherConceptsHandlers.onError)

    // Handle API responses for propositions (factorized)
    const conceptReviewHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'concept_review'), [createMaterialUpdateHandlers])
    const methodReviewHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'method_review'), [createMaterialUpdateHandlers])
    const firstSubExerciseHandlers = useMemo(() => createMaterialUpdateHandlers('variation', 'first_sub_exercise'), [createMaterialUpdateHandlers])

    useApiStatus(activitiesService.generatePropositionConceptReview, null, false, conceptReviewHandlers.onSuccess, conceptReviewHandlers.onError)
    useApiStatus(activitiesService.generatePropositionMethodReview, null, false, methodReviewHandlers.onSuccess, methodReviewHandlers.onError)
    useApiStatus(activitiesService.generatePropositionFirstSubExercise, null, false, firstSubExerciseHandlers.onSuccess, firstSubExerciseHandlers.onError)

    // Initialize similar exercises when mode is 'similar'
    useEffect(() => {
        if (mode === "similar" && materialId && step === "results") {
            // Base request data with optional instructions and student_interests
            // target_level + duration are sent to AI so the 3 generated exercises
            // align with the tutor's chosen difficulty level and duration.
            const targetLevel = selectedLevel ? parseInt(selectedLevel, 10) : null
            const targetDuration = selectedDuration ? parseInt(selectedDuration, 10) : null

            const baseRequestData = {
                ...(targetLevel && { target_level: targetLevel }),
                ...(targetDuration && { duration: targetDuration }),
                ...(similarInstructions && { instructions: similarInstructions }),
                ...(similarStudentInterests && { student_interests: similarStudentInterests }),
                // Alimente l'historique anti-répétition élève×chapitre côté backend.
                ...(historyStudentIds.length > 0 && { student_ids: historyStudentIds }),
            }

            const requestData = {
                material_id: materialId,
                ...baseRequestData,
            }

            if (selectedLevel === "1") {
                // Révision du cours: concept_review, method_review, same_difficulty (simple)
                setMaterials([
                    { id: 1, status: "pending", variation: "concept_review" },
                    { id: 2, status: "pending", variation: "method_review" },
                    { id: 3, status: "pending", variation: "same_difficulty" },
                ])

                props.apiRequest(
                    activitiesService.generatePropositionConceptReview,
                    { materialId: materialId },
                    { num_clusters: 1, ...baseRequestData }
                )
                props.apiRequest(
                    activitiesService.generatePropositionMethodReview,
                    { materialId: materialId },
                    { num_clusters: 1, ...baseRequestData }
                )
                props.apiRequest(
                    activitiesService.generateSimilarExercisesSameDifficulty,
                    {},
                    requestData
                )
            } else {
                // Entraînement (2) & Approfondissement (3):
                // exo_fidèle | exo_autre_notion | exo_fidèle diversifié (exercise_type/context)
                // All three aligned to the tutor's chosen target_level.
                setMaterials([
                    { id: 1, status: "pending", variation: "same_difficulty" },
                    { id: 2, status: "pending", variation: "other_concepts" },
                    { id: 3, status: "pending", variation: "same_difficulty_diversified" },
                ])

                props.apiRequest(
                    activitiesService.generateSimilarExercisesSameDifficulty,
                    {},
                    requestData
                )
                props.apiRequest(
                    activitiesService.generateSimilarExercisesOtherConcepts,
                    {},
                    requestData
                )
                props.apiRequest(
                    activitiesService.generateSimilarExercisesSameDifficultyDiversified,
                    {},
                    requestData
                )
            }
        }
    }, [mode, materialId, step, student?.id, selectedLevel, selectedDuration, similarInstructions, similarStudentInterests])

    // True when the (class, subject) scope has 0 validated chapters in DB.
    // Still used by the image flow's gate below: a dropped photo has no
    // instructions field to fall back on, so an empty catalogue is the only
    // thing that can make a chapter optional there.
    // The loading flag avoids a spurious "empty" verdict during the fetch.
    const scopeHasNoChapters =
        !!watchedClass &&
        !!watchedSubject &&
        !props.getChaptersRequest &&
        chapters.length === 0

    // The image flow's chapter gate — what "Créer l'activité" and the two
    // parcours buttons wait for. It reads `activeChapterId`, which already
    // resolves to the program chapter in study-program mode and to the manual
    // dropdown otherwise, so both modes answer the same question: is a chapter
    // pinned? Gating on `watchedChapter` instead left every image action
    // permanently disabled under a study program, where that field is never
    // filled and no dropdown exists to fill it — the tutor's only way out was
    // to switch the program off and start over.
    const imageFlowChapterMissing = !scopeHasNoChapters && !activeChapterId

    // Requiring the dropdown whenever the catalogue wasn't empty left a tutor
    // whose topic wasn't listed on a disabled "Générer", with nothing saying
    // Instructions would have worked. An out-of-program topic goes through
    // ChapterResolver, which creates a `source=user_generated` chapter.
    const isFormValid = isGenerationFormValid({
        useStudyProgram,
        studyProgramChapter: watchedStudyProgramChapter,
        schoolClass: watchedClass,
        subject: watchedSubject,
        chapter: watchedChapter,
        instructions: watchedInstructions,
    })

    const onSubmit = (data) => {
        // Sam Prescription embedding: bypass the 3-cards flow entirely and
        // hand the form values back to the caller. The caller (Sam screen)
        // calls its own endpoint (study-missions/regenerate) and the modal
        // closes immediately — no MaterialResultsCards step here.
        if (onSubmitOverride) {
            // Seulement ce que le tuteur a choisi : le formulaire s'ouvre
            // pré-rempli, et renvoyer ces valeurs telles quelles ferait passer
            // un pré-remplissage pour une décision.
            onSubmitOverride(
                buildRegeneratePayload({ data, dirtyFields, useStudyProgram })
            )
            onClose()
            return
        }

        setStep("results")

        // Make API call to generate activities
        const baseRequestData = useStudyProgram
            ? {
                  class: data.studyProgramChapter?.class,
                  school_subject_id:
                      data.studyProgramChapter?.school_subject_id,
                  instructions: data.instructions,
                  chapter_id: data.studyProgramChapter?.id || null,
                  level: data.selectedLevel,
                  duration: parseInt(data.selectedDuration, 10),
                  // Alimente l'historique anti-répétition élève×chapitre côté backend.
                  ...(historyStudentIds.length > 0 && { student_ids: historyStudentIds }),
              }
            : {
                  class: data.selectedClass,
                  school_subject_id: data.selectedSubject,
                  instructions: data.instructions,
                  chapter_id: data.selectedChapter || null,
                  level: data.selectedLevel,
                  duration: parseInt(data.selectedDuration, 10),
                  ...(historyStudentIds.length > 0 && { student_ids: historyStudentIds }),
              }

        // Add student interests if personalization is enabled
        let requestData = personalizeWithInterests && student?.profile?.interests
            ? { ...baseRequestData, student_interests: student.profile.interests }
            : baseRequestData

        // Forward the tutor-selected positions so the backend filters the
        // chapter's skill_parts before sending to the AI service.
        if (selectedSkillPartPositions.length > 0) {
            requestData = {
                ...requestData,
                skill_part_positions: selectedSkillPartPositions,
            }
        }

        lastGenerationRequestRef.current = requestData
        launchGenerationCards([1, 2, 3])
    }

    // Les cartes d'une demande d'énoncés (1, 2, 3 = `skills_part`). Le backend sert
    // d'abord celles que la réserve a déjà pour ces paramètres et ne génère que les
    // manquantes ; `force` (« Autres énoncés », ou ↻ sur une carte) la saute.
    const launchGenerationCards = (cardIndexes, { force = false } = {}) => {
        // ALWAYS fire 3 card slots regardless of chip selection size. The
        // AI prompt (Fix C1) handles the modulo + variation logic when fewer
        // than 3 skills are selected — same skill, different exercise_type
        // / exercise_context per card. We pass `skill_part_positions` once;
        // each of the 3 calls only differs by `skills_part` (1, 2, 3).
        const isFullBatch = cardIndexes.length === 3
        setMaterials((prev) =>
            isFullBatch
                ? cardIndexes.map((i) => ({ id: i, status: "pending", skills_part: i }))
                : prev.map((m) =>
                      cardIndexes.includes(m.skills_part)
                          ? { id: m.skills_part, status: "pending", skills_part: m.skills_part }
                          : m
                  )
        )

        // Tag the parallel calls with a shared batch id so the backend can
        // deterministically pick mutually-exclusive exercise formats per card
        // when several cards share the same skill_part. The first call
        // computes & caches the picks (60s TTL), the others read the cache.
        if (isFullBatch || !lastBatchIdRef.current) {
            lastBatchIdRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
        }
        const batchRequestData = {
            ...lastGenerationRequestRef.current,
            request_batch_id: lastBatchIdRef.current,
            ...(force && { force_generation: true }),
        }

        // Map skills_part index → service. The 3 generate-* services exist for
        // historical reasons (Redux SUCCESS dispatch keying); we only call as
        // many as we have card slots.
        const serviceBySkillsPart = {
            1: activitiesService.generateMaterialsSkillsPart1,
            2: activitiesService.generateMaterialsSkillsPart2,
            3: activitiesService.generateMaterialsSkillsPart3,
        }
        cardIndexes.forEach((skillsPart) => {
            const svc = serviceBySkillsPart[skillsPart]
            if (!svc) return
            props.apiRequest(svc, { id: student?.id }, batchRequestData)
        })
    }

    const canRegenerate = mode !== "similar" && !imageMode && lastGenerationRequestRef.current != null
    const cardsPending = materials.some((m) => m.status === "pending")
    const regenerateCards = () => launchGenerationCards([1, 2, 3], { force: true })
    const replaceCard = (material) => launchGenerationCards([material.skills_part], { force: true })

    // Handle create activity API success (single student)
    useApiStatus(
        activitiesService.createActivityAsTutor,
        null,
        false,
        (data) => {
            // Check if this is from image flow
            if (creatingActivity) {
                setCreatingActivity(false)
                setActivityCreated(true)

                if (data?.material) {
                    setMaterialFromImage(prev => ({
                        ...prev,
                        ...data.material,
                    }))
                }

                // Send live session notification if in live mode
                if (props.isLiveMode) {
                    sendNewTodo(
                        "newTodo",
                        {
                            id: student?.id,
                            activity: data,
                        },
                        [student?.id]
                    )
                }

                // Refresh student data to update chat message display
                if (student?.id) {
                    props.apiRequest(studentsService.getStudent, { id: student.id })
                }

                // Don't close modal, just show success state
                return
            }

            // Find which material this success is for (standard flow)
            const materialId = Object.keys(loadingStates).find(
                (id) => loadingStates[id]
            )
            if (materialId) {
                // Update material status - if we get here, activity (and quiz if requested) was created successfully
                setMaterials((prev) =>
                    prev.map((m) =>
                        m.id === parseInt(materialId)
                            ? { ...m, createStatus: "created" }
                            : m
                    )
                )

                // Clear states - no need to check for quiz errors here since backend uses transaction
                // If quiz fails, the whole request fails (handled in error callback)
                setLoadingStates((prev) => ({ ...prev, [materialId]: false }))
                setPendingQuizMaterials((prev) => ({ ...prev, [materialId]: false }))

                // Send live session notification if in live mode
                if (props.isLiveMode) {
                    sendNewTodo(
                        "newTodo",
                        {
                            id: student?.id,
                            activity: data,
                        },
                        [student?.id]
                    )
                }
            }
        },
        (error) => {
            props.apiIdle(activitiesService.createActivityAsTutor)

            // Clear loading state on error
            if (creatingActivity) {
                setCreatingActivity(false)
                // Check if quiz was enabled for image flow and set error
                if (imageFlowQuizEnabled) {
                    setImageFlowQuizError(true)
                }
                return
            }

            const materialId = Object.keys(loadingStates).find(
                (id) => loadingStates[id]
            )
            if (materialId) {
                setLoadingStates((prev) => ({ ...prev, [materialId]: false }))


                setPendingQuizMaterials((prev) => {
                    if (prev[materialId]) {
                        setQuizErrorStates((prevErrors) => ({ ...prevErrors, [materialId]: true }))
                    }
                    return { ...prev, [materialId]: false }
                })
            }
        }
    )

    // Handle create activity API success (student self-create)
    useApiStatus(
        activitiesService.activityTodoAsStudent,
        null,
        false,
        (data) => {
            // currentUser reducer's activityTodoAsStudent SUCCESS case
            // appends the new activity to todo_activities, so no refetch
            // is needed — the list updates as soon as the modal closes.

            if (creatingActivity) {
                setCreatingActivity(false)
                setActivityCreated(true)
                if (data?.material) {
                    setMaterialFromImage(prev => ({
                        ...prev,
                        ...data.material,
                    }))
                }
                return
            }

            const materialId = Object.keys(loadingStates).find(
                (id) => loadingStates[id]
            )
            if (materialId) {
                setMaterials((prev) =>
                    prev.map((m) =>
                        m.id === parseInt(materialId)
                            ? { ...m, createStatus: "created" }
                            : m
                    )
                )
                setLoadingStates((prev) => ({ ...prev, [materialId]: false }))
                setPendingQuizMaterials((prev) => ({ ...prev, [materialId]: false }))
            }
        },
        (error) => {
            props.apiIdle(activitiesService.activityTodoAsStudent)

            // Surface the backend message to the student — most notably the
            // 20-activity limit (403 "Tu ne peux pas ajouter plus de 20
            // activités à faire."). SnackBarM, not DialogM, since we're
            // already inside a modal.
            if (error?.message) {
                SnackBarM.show({
                    type: "error",
                    text1: error.message,
                })
            }

            if (creatingActivity) {
                setCreatingActivity(false)
                if (imageFlowQuizEnabled) {
                    setImageFlowQuizError(true)
                }
                return
            }

            const materialId = Object.keys(loadingStates).find(
                (id) => loadingStates[id]
            )
            if (materialId) {
                setLoadingStates((prev) => ({ ...prev, [materialId]: false }))
                setPendingQuizMaterials((prev) => {
                    if (prev[materialId]) {
                        setQuizErrorStates((prevErrors) => ({ ...prevErrors, [materialId]: true }))
                    }
                    return { ...prev, [materialId]: false }
                })
            }
        }
    )

    // Handle create activity API success (multi-student)
    useApiStatus(
        activitiesService.activityTodoForStudents,
        null,
        false,
        (data, all_data) => {
            // all_data contains the full response (new_activities, live_session)
            // data is response.data.data (undefined if not wrapped), all_data is response.data
            const responseData = data || all_data

            // Handle image flow (file upload path)
            if (creatingActivity) {
                setCreatingActivity(false)
                setActivityCreated(true)

                const firstActivity = responseData?.new_activities?.[0]
                if (firstActivity?.material) {
                    setMaterialFromImage(prev => ({
                        ...prev,
                        ...firstActivity.material,
                    }))
                }

                // Send live session notification if in live mode - send to each student
                if (props.isLiveMode && responseData?.new_activities) {
                    responseData.new_activities.forEach((activity) => {
                        sendNewTodo(
                            "newTodo",
                            {
                                id: activity?.student_id,
                                activity,
                            },
                            [activity?.student_id]
                        )
                    })
                }

                return
            }

            // Get material_id from the first created activity (all activities share the same material)
            const createdMaterialId = responseData?.new_activities?.[0]?.material?.id

            console.log('🔍 Material ID from response:', createdMaterialId)

            if (createdMaterialId) {
                // Update material status - if we get here, activities (and quizzes if requested) were created successfully
                setMaterials((prev) =>
                    prev.map((m) =>
                        m.id === createdMaterialId
                            ? { ...m, createStatus: "created" }
                            : m
                    )
                )

                // Clear states - no need to check for quiz errors here since backend uses transaction
                // If quiz fails, the whole request fails (handled in error callback)
                setLoadingStates((prev) => ({ ...prev, [createdMaterialId]: false }))
                setPendingQuizMaterials((prev) => ({ ...prev, [createdMaterialId]: false }))

                // Send live session notification if in live mode - send to each student
                if (props.isLiveMode && responseData?.new_activities) {
                    responseData.new_activities.forEach((activity) => {
                        sendNewTodo(
                            "newTodo",
                            {
                                id: activity?.student_id,
                                activity,
                            },
                            [activity?.student_id]
                        )
                    })
                }
            }
        },
        (error) => {
            props.apiIdle(activitiesService.activityTodoForStudents)

            // Handle image flow error
            if (creatingActivity) {
                setCreatingActivity(false)
                if (imageFlowQuizEnabled) {
                    setImageFlowQuizError(true)
                }
                return
            }

            // Find which material had the error and set quiz error if applicable
            const materialId = Object.keys(loadingStates).find(
                (id) => loadingStates[id]
            )
            if (materialId) {
                setPendingQuizMaterials((prev) => {
                    if (prev[materialId]) {
                        setQuizErrorStates((prevErrors) => ({ ...prevErrors, [materialId]: true }))
                    }
                    return { ...prev, [materialId]: false }
                })
            }
            // Clear all loading states on error
            setLoadingStates({})
        }
    )

    const createActivity = (material, hasPrerequisiteQuiz = false) => {
        // Set loading state for this specific material
        setLoadingStates((prev) => ({ ...prev, [material.id]: true }))
        setQuizErrorStates((prev) => ({ ...prev, [material.id]: false }))
        setPendingQuizMaterials((prev) => ({ ...prev, [material.id]: hasPrerequisiteQuiz }))

        // Determine if it's a SubMaterial or Material
        const baseMaterialData = material.sub_material_id
            ? { sub_material_id: material.sub_material_id }
            : { material_id: material.id }

        // Add prerequisite quiz flag if enabled
        const materialData = {
            ...baseMaterialData,
            has_prerequisite_quiz: hasPrerequisiteQuiz,
        }

        if (selfCreate) {
            // Student creates for self — no URL student id, gated by create_own_challenges
            props.apiRequest(
                activitiesService.activityTodoAsStudent,
                {},
                materialData
            )
        } else if (forStudents && selectedStudentIds.length > 0) {
            // Multi-student creation
            const requestData = {
                ...materialData,
                student_ids: selectedStudentIds,
                live_session_id: props.isLiveMode ? props.liveSessionId : null,
            }

            props.apiRequest(
                activitiesService.activityTodoForStudents,
                {},
                requestData
            )
        } else {
            // Single student creation
            props.apiRequest(
                activitiesService.createActivityAsTutor,
                { id: student?.id },
                materialData
            )
        }
    }

    const navigation = nav ?? useNavigation()

    // Maintain a mapping of unique skillsText to a sequential color index
    const skillsColorMapRef = useRef({})
    const getSkillsBackgroundColor = (skillsText, baseColor) => {
        if (!skillsText || !baseColor) return theme.colors.lightMore

        // Fixed lightening percentage
        const lightPercent = 0.4
        // Hue shift variations [-10°, 0°, +10°]
        const hueShifts = [-10, 0, 10]
        if (!(skillsText in skillsColorMapRef.current)) {
            skillsColorMapRef.current[skillsText] = Object.keys(
                skillsColorMapRef.current
            ).length
        }
        const idx = skillsColorMapRef.current[skillsText]
        const shift = hueShifts[idx % hueShifts.length]

        return createColorVariation(baseColor, lightPercent, shift)
    }

    // For existing messages/conversations, use messageId/conversationId instead of file upload
    const hasMessageId = !!uploadedImage?.messageId
    const hasConversationId = !!uploadedImage?.conversationId
    const hasExistingFile = hasMessageId || hasConversationId
    const filesToUpload = (uploadedImage && !hasExistingFile) ? [{
        uri: uploadedImage.uri,
        name: uploadedImage.name,
        type: uploadedImage.mimeType || 'image/jpeg'
    }] : []

    // v3 single-call pipeline: upload to Vapor tmp, then POST to /materials/analyze
    // which runs OCR + the analyzeMaterial LLM call in one round-trip. Based on
    // the AI confidence, the result either auto-pre-selects the chapter (high)
    // or surfaces it inline inside the chapter dropdown's open list (medium);
    // low confidence is silent. See ChapterSearchDropdown's `aiSuggestions`
    // prop and the auto-select effect on `analysisStatus === 'done'` below.
    const [imageUploadProgress, imageUploadSubmitted, startImageUpload] = useRequestsBatch(
        [],
        filesToUpload,
        "IMAGE_MATERIAL_UPLOAD",
        {
            service: materialsService.analyzeMaterial,
            params: {},
            data: {
                student_id: resolvedStudent?.id,
                student_level: watchedClass || resolvedStudent?.profile?.class || "3ème",
                // school_subject_id is required by the analyze endpoint —
                // tutors choose class+subject up-front in the modal.
                school_subject_id: watchedSubject ? parseInt(watchedSubject, 10) : null,
                ...(hasMessageId && { message_id: uploadedImage.messageId }),
                ...(hasConversationId && { conversation_id: uploadedImage.conversationId }),
            },
            tmpFilesKey: "image_files",
            tmpFilesFormatter: (tmpKey) => tmpKey,
            additionalData: [],
            onSuccessBeforeNext: (data) => {
                const material = data?.data?.data || data?.data || data
                if (!material?.id) {
                    console.error("[analyze] No material ID in response")
                }
                setMaterialFromImage(material)
                applyAnalysisToForm(material)
            },
            onError: (error) => {
                console.error("[analyze] Pipeline failed", error)
                const errorMessage = error?.response?.data?.error ||
                                    error?.error ||
                                    error?.message ||
                                    "Une erreur est survenue lors de l'analyse de l'image"

                // Soft-fail: the manual MaterialGenerationForm path remains
                // fully usable; we only surface a small inline error.
                const materialInError = error?.data?.data || error?.data
                if (materialInError?.id) {
                    setMaterialFromImage(materialInError)
                }
                setAnalysisError(errorMessage)
                setAnalysisStatus('error')
                setAnalysisSuggestion(null)
            }
        },
        []
    )

    /**
     * Translate a Material returned by /api/v2/materials/analyze into the
     * MaterialGenerationForm state. Non-destructive: if the tutor has already
     * picked a chapter manually we silently dismiss the suggestion (no chip).
     */
    const applyAnalysisToForm = (material) => {
        const analysis = material?.structured_data?.analysis
        const altChapters = material?.structured_data?.analysis_alternatives?.chapters || []

        if (material?.title) {
            setActivityTitle(material.title)
        }

        // If the tutor already chose a chapter while the analysis was running,
        // do not surprise them with a chip. They are in control — and that
        // extends to the class: correcting it here would reset `selectedChapter`
        // (see the sync block below) and wipe out their manual pick with
        // nothing to restore it.
        if (watchedChapter) {
            setAnalysisStatus('done')
            setAnalysisSuggestion(null)
            return
        }

        // Class correction is silent and
        // automatic, no chip — the backend may have corrected `material.class`
        // to an adjacent class when the chapter match landed there (see
        // ExtractionController::adjacentClasses). Reflect it in the form here,
        // otherwise "Classe" keeps showing the tutor's original, now-stale pick
        // even though the persisted material (and its chapter) are correct.
        //
        // Changing `selectedClass` re-triggers the [watchedClass, watchedSubject]
        // effect below, which resets `selectedChapter` to null and refetches
        // the chapters list for the new scope — wiping out a high-confidence
        // chapter auto-select landing in the same tick. Queue the chapter id
        // through the same `pendingChapterIdRef` mechanism handleGlobalChapterSelect
        // already uses for this exact race, so the refetch's success handler
        // re-applies it once the corrected scope's chapters have loaded.
        if (material?.class && material.class !== watchedClass) {
            if (analysis?.confidence === 'high' && analysis?.chapter_id) {
                pendingChapterIdRef.current = analysis.chapter_id.toString()
            }
            setValue("selectedClass", material.class)
        }

        const suggestionChapter = analysis?.chapter_id
            ? {
                id: analysis.chapter_id,
                name: material?.chapter_model?.name || material?.chapter_name || `Chapitre ${analysis.chapter_id}`,
                parent_name: material?.chapter_model?.parent_name || null,
            }
            : null

        setAnalysisSuggestion({
            suggestion: suggestionChapter,
            alternatives: altChapters,
            confidence: analysis?.confidence || 'low',
            level: analysis?.level || null,
            title: material?.title || null,
            chapter_skills: material?.chapter_skills || [],
            material_skills: material?.material_skills || [],
            // Volet 2 — the real subject the AI thinks the
            // content is, independent of the chapter match within the tutor's
            // chosen scope above. See the scope-mismatch effect below. Class
            // correction (09/10, Wajih) is no longer a separate detected field
            // — it rides silently on the chapter match itself (adjacent-class
            // chapters), so there's nothing to carry here for it.
            detectedSubjectId: analysis?.detected_subject_id ?? null,
            subjectMatchConfidence: analysis?.subject_match_confidence || 'low',
        })
        setAnalysisStatus('done')
    }

    // Scope-mismatch suggestion: analyze()
    // returns detected_subject_id/subject_match_confidence directly (volet 2
    // of analyze-material, independent of the chapter search run within the
    // tutor's chosen scope). No search call needed anymore — the detection
    // already came back in the same response as the chapter match; this only
    // derives the chip's display state from it, and only when the detection
    // is confident (sans proposer de chapitre — "Basculer" re-runs the
    // chapter search in the new scope via reanalyzeScope()). Pure derivation
    // of already-available values — a useMemo, not state+effect.
    //
    // Class is deliberately NOT part of this chip (09/10, Wajih): a bare
    // "which class" guess on a name list is unreliable — too many chapters
    // share the exact same name across adjacent classes for the LLM to tell
    // them apart without seeing real chapter content. The backend now widens
    // the chapter search itself to the adjacent classes and silently corrects
    // `material.class` when the match lands there — see
    // ExtractionController::adjacentClasses(). Nothing for the frontend to
    // suggest or switch for class; it just reflects whatever class comes back
    // in the material after analyze()/reanalyzeScope().
    //
    // Declared before handleScopeSwitchAccept below, which reads it.
    const crossClassSuggestion = useMemo(() => {
        if (!imageMode) return null
        if (!uploadedImage) return null // image removed — nothing to suggest a scope for
        // Not in study-program mode: "Basculer" jumps matière, which the
        // program chapter owns there — the chip has nowhere to render anyway.
        if (useStudyProgram) return null
        if (analysisStatus !== 'done') return null
        if (crossClassDismissed) return null // tutor explicitly ignored it
        if (watchedChapter) return null // tutor manually picked something

        const detectedSubjectId = analysisSuggestion?.detectedSubjectId
        const confidence = analysisSuggestion?.subjectMatchConfidence
        if (confidence !== 'high' || !detectedSubjectId) return null
        if (String(detectedSubjectId) === String(watchedSubject)) return null

        return {
            school_subject_id: detectedSubjectId,
            school_subject_name:
                schoolSubjects?.find((s) => String(s.id) === String(detectedSubjectId))?.name || null,
        }
    }, [
        imageMode,
        uploadedImage,
        useStudyProgram,
        analysisStatus,
        analysisSuggestion,
        watchedSubject,
        watchedChapter,
        crossClassDismissed,
        schoolSubjects,
    ])

    // Success metric: chip display is the moment a mismatch was
    // actually caught and offered a one-click correction. Isolated from the
    // useMemo above — tracking is a side effect, not part of the derivation.
    useEffect(() => {
        if (!crossClassSuggestion) return
        amplitude.track('material_scope_suggestion.shown')
    }, [crossClassSuggestion])

    // "Basculer" on the scope-mismatch suggestion (step 2): no new OCR —
    // reanalyzeScope() re-runs the chapter search (now widened to adjacent
    // classes server-side) on the transcript already stored on the material,
    // scoped to the corrected subject. We sync the form's subject right away
    // so the chapter dropdown's own refetch effect keeps the manual list in
    // sync; the chapter (and class, if the match lands on a neighbor) comes
    // back from the response via applyAnalysisToForm, same as the first pass.
    const handleScopeSwitchAccept = useCallback(() => {
        if (!crossClassSuggestion?.school_subject_id) return
        if (!materialFromImage?.id) return
        amplitude.track('material_scope_suggestion.accepted')
        setCrossClassLoading(true)
        setValue("selectedSubject", crossClassSuggestion.school_subject_id.toString())
        props.apiRequest(
            materialsService.reanalyzeScope,
            { id: materialFromImage.id },
            {
                school_subject_id: crossClassSuggestion.school_subject_id,
                student_level: watchedClass,
            }
        )
    }, [crossClassSuggestion, materialFromImage?.id, watchedClass, setValue])

    useApiStatus(
        materialsService.reanalyzeScope,
        null,
        false,
        (data) => {
            const material = data?.data?.data || data?.data || data
            if (material?.id) {
                setMaterialFromImage(material)
                applyAnalysisToForm(material)
            }
            setCrossClassLoading(false)
        },
        (error) => {
            console.error("[reanalyze-scope] failed", error)
            // Opt out of useApiStatus's own dialog (see handleImageChapterSelect's
            // updateMaterialChapter handler above for why apiIdle must be called
            // by hand in that case) and surface a lighter inline snackbar instead.
            props.apiIdle(materialsService.reanalyzeScope)
            setCrossClassLoading(false)
            SnackBarM.show({
                type: "error",
                text1: "Impossible de relancer l'analyse dans la nouvelle matière.",
            })
        }
    )

    // Auto-pre-select the chapter when the AI is highly confident — the
    // tutor sees the chapter chip already populated in the dropdown and can
    // clear it with the dropdown's standard ✕ if they disagree. Medium
    // confidence renders the suggestion inside the dropdown's open list (see
    // ChapterSearchDropdown's aiSuggestions prop). Low confidence: nothing.
    //
    // Idempotency: we track the analysis key (material id, falling back to the
    // analysis title) in a ref so the effect only fires ONCE per analysis. If
    // we re-fired on every render, clearing the chip via ✕ would flip
    // watchedChapter to null, which would re-trigger this effect and set the
    // chip back — making the × button feel broken.
    useEffect(() => {
        // Never in study-program mode: there is no chapter dropdown to show the
        // chip in, so this would only plant a manual chapter the tutor can't see
        // — and it would hide the "analyse running / failed" feedback below,
        // which is gated on selectedChapter being empty.
        if (useStudyProgram) return
        if (analysisStatus !== 'done') return
        if (analysisSuggestion?.confidence !== 'high') return
        const suggestionId = analysisSuggestion?.suggestion?.id
        if (!suggestionId) return
        const analysisKey = materialFromImage?.id ?? analysisSuggestion?.title ?? null
        if (analysisKey === null) return
        if (autoSelectedAnalysisKeyRef.current === analysisKey) return
        autoSelectedAnalysisKeyRef.current = analysisKey
        setValue("selectedChapter", suggestionId.toString())
        if (analysisSuggestion?.level) {
            setValue("selectedLevel", String(analysisSuggestion.level))
        }
        if (analysisSuggestion?.title) {
            setActivityTitle(analysisSuggestion.title)
        }
    }, [
        analysisStatus,
        analysisSuggestion,
        materialFromImage?.id,
        setValue,
        useStudyProgram,
    ])

    // Initialize with initialImage prop if provided (from chat flow)
    useEffect(() => {
        if (isOpen && initialImage && !uploadedImage) {
            const isPDF = isPdfFile(initialImage)

            if (IS_WEB && !isPDF) {
                // On web with image: show crop modal first
                setImageToCrop(initialImage)
                setShowCropModal(true)
            } else {
                // On mobile or PDF: proceed directly (no crop for PDF)
                setUploadedImage(initialImage)
                setImageMode(true)
            }
        }
    }, [isOpen, initialImage])

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            forgetPickedDuration()
            lastStartedUploadRef.current = null
            autoSelectedAnalysisKeyRef.current = null
            pendingSkillPartPositionRef.current = null
            chapterAnchorRef.current = null
            setFailedAnchorChapterId(null)
            setImageMode(false)
            setUploadedImage(null)
            setMaterialFromImage(null)
            setCreatingActivity(false)
            setActivityCreated(false)
            setAnalysisError(null)
            setActivityTitle("")
            setAnalysisStatus('idle')
            setAnalysisSuggestion(null)
            setCrossClassDismissed(false)
            setCrossClassLoading(false)
            setMaterials([])
            lastGenerationRequestRef.current = null
            setLoadingStates({})
            setQuizErrorStates({})
            setPendingQuizMaterials({})
            setStep("form")
            setChapters([])
            setImageToCrop(null)
            setShowCropModal(false)
            if (forStudents) {
                setSelectedStudentIds(studentsList?.map(s => s.id) || [])
                setShowStudentSelection(false)
            }
            setImageFlowQuizEnabled(false)
            setImageFlowQuizError(false)
            setShowCustomParcours(false)
            setCustomLevel("2")
            setCustomInstructions("")
        }
    }, [isOpen, forStudents, studentsList])

    // Trigger upload when image is selected. Non-blocking: the form (chapter
    // dropdown, class/subject) stays interactive while OCR + LLM run.
    // The analyze endpoint requires class + school_subject_id — both are
    // pre-filled from the student's profile so this is satisfied in practice.
    useEffect(() => {
        if (uploadedImage && !imageUploadSubmitted) {
            if (uploadedImage === lastStartedUploadRef.current) {
                return
            }

            // Hard requirement of the analyze endpoint: tutor must have picked
            // a subject. Defer the upload until they do — no point shipping a
            // request that will 422.
            if (!watchedClass || !watchedSubject) {
                return
            }

            lastStartedUploadRef.current = uploadedImage
            // A fresh analysis is starting — let the auto-select effect fire
            // for it once we have the result.
            autoSelectedAnalysisKeyRef.current = null
            // Any anchor in flight belongs to the material being replaced.
            chapterAnchorRef.current = null
            setFailedAnchorChapterId(null)

            props.apiIdle(materialsService.analyzeMaterial)

            setMaterialFromImage(null)
            setAnalysisError(null)
            setAnalysisStatus('analyzing')
            setAnalysisSuggestion(null)
            setCrossClassDismissed(false)
            setCrossClassLoading(false)
            setImageFlowQuizEnabled(false)
            setImageFlowQuizError(false)
            startImageUpload()
        }
    }, [uploadedImage, imageUploadSubmitted, watchedClass, watchedSubject])

    // Reset analysis + cross-class state when the tutor removes the image or
    // changes the class/subject scope. The analyze response is keyed to the
    // class+subject that was active at upload time; once the scope shifts, the
    // suggestions become stale.
    useEffect(() => {
        if (!uploadedImage) {
            setCrossClassDismissed(false)
            setCrossClassLoading(false)
            // Without a current image, any future analysis is a fresh one.
            autoSelectedAnalysisKeyRef.current = null
        }
    }, [uploadedImage])

    useEffect(() => {
        // Class/subject change: drop any cross-class suggestion. The analyze
        // pipeline will refire if a fresh image is uploaded under the new
        // scope. We do NOT clear analysisSuggestion here — the analyze flow
        // owns its own lifecycle (see uploaded-image effect above). The chip's
        // own useMemo recomputes against the new watchedSubject automatically
        // — no explicit clear needed.
        setCrossClassDismissed(false)
        setCrossClassLoading(false)

        // When the tutor changes class or subject, the previously selected
        // chapter no longer belongs to the active scope. Clear the form value
        // so the chapter chip + the dependent skill_parts picker (which is
        // gated on activeChapterId in the chapter-change effect above) both
        // collapse. Without this, watchedChapter stays set even though the
        // chapter list is empty for the new scope, and the tutor can submit
        // a stale chapter id.
        //
        // EXCEPTION: when handleGlobalChapterSelect triggers a cross-class
        // jump, it sets pendingChapterIdRef BEFORE updating class/subject so
        // the chapters refetch can re-apply the queued chapter on success.
        // We must not clobber that queued chapter here — leave the form value
        // alone and let the refetch flow run. The pre-existing
        // [watchedClass, watchedSubject] effect that calls
        // setValue("selectedChapter", null) (when both are present) is also
        // gated by this same ref via the success handler at the chapters
        // service callback above.
        if (pendingChapterIdRef.current) {
            return
        }
        setValue("selectedChapter", null)
        // Drop the auto-select memo so the next analysis under a new scope
        // can fire fresh.
        autoSelectedAnalysisKeyRef.current = null
    }, [watchedClass, watchedSubject, setValue])

    // Handler for "Create Activity" button in image flow
    const handleCreateActivityFromImage = (hasPrerequisiteQuiz = false) => {
        if (!materialFromImage?.id) {
            console.error("No material found for activity creation")
            return
        }

        setCreatingActivity(true)
        setImageFlowQuizError(false)

        // Forward the tutor's explicit matière + chapitre selection so the
        // activity is recorded under the subject they actually picked. The
        // material's subject was frozen at analyze time from the default
        // subject (e.g. français); without these the backend would create the
        // activity from the material's stale default and silently ignore the
        // tutor's manual change. The backend syncs the material to this
        // selection before creating, keeping material/activity/objectif coherent.
        //
        // In study-program mode the chapter the tutor pinned is the selection —
        // there is no chapter dropdown to hold a manual one. Without this the
        // activity would silently land on whatever chapter the analyse detected,
        // which the tutor never saw and cannot correct.
        const chapterIdToSend = useStudyProgram
            ? watchedStudyProgramChapter?.id
            : watchedChapter
        const requestData = {
            material_id: materialFromImage.id,
            estimated_duration: parseInt(getValues("selectedDuration"), 10),
            material_title: activityTitle?.trim() || undefined,
            has_prerequisite_quiz: hasPrerequisiteQuiz,
            ...(watchedSubject
                ? { school_subject_id: parseInt(watchedSubject, 10) }
                : {}),
            ...(chapterIdToSend
                ? { chapter_id: parseInt(chapterIdToSend, 10) }
                : {}),
        }

        if (selfCreate) {
            props.apiRequest(
                activitiesService.activityTodoAsStudent,
                {},
                requestData
            )
        } else if (forStudents && selectedStudentIds.length > 0) {
            props.apiRequest(
                activitiesService.activityTodoForStudents,
                {},
                {
                    ...requestData,
                    student_ids: selectedStudentIds,
                    live_session_id: props.isLiveMode ? props.liveSessionId : null,
                }
            )
        } else {
            props.apiRequest(
                activitiesService.createActivityAsTutor,
                { id: student?.id },
                requestData
            )
        }
    }

    // Handler for chapter selection from ChapterSearchModal
    const handleImageChapterSelect = (chapterInfos) => {
        if (!materialFromImage?.id || !chapterInfos?.id) {
            console.error("Missing material or chapter for update")
            return
        }

        // Call API to update the material's chapter
        props.apiRequest(
            materialsService.updateMaterialChapter,
            { id: materialFromImage.id },
            { chapter_id: chapterInfos.id }
        )

        // Update materialFromImage with new chapter info
        setMaterialFromImage(prev => ({
            ...prev,
            chapter_id: chapterInfos.id,
            school_subject_id: chapterInfos.school_subject_id,
        }))
    }

    // Handle API response for chapter update
    useApiStatus(
        materialsService.updateMaterialChapter,
        null,
        false,
        () => {
            // Only now is the material really on that chapter — writing it
            // earlier would make the anchor guard believe a failed request had
            // succeeded, and nothing would retry it.
            const pending = chapterAnchorRef.current
            chapterAnchorRef.current = null
            if (!pending) return
            setMaterialFromImage((prev) =>
                prev && prev.id === pending.materialId
                    ? {
                          ...prev,
                          chapter_id: pending.chapter.id,
                          school_subject_id:
                              pending.chapter.school_subject_id ??
                              prev.school_subject_id,
                      }
                    : prev
            )
        },
        (error) => {
            console.error("Failed to update chapter", error)
            // `useApiStatus` only clears the entry when it shows its own
            // dialog, which we opt out of. Left behind, the error replays on
            // every later mount of this modal and raises the banner for a
            // request that was never made.
            props.apiIdle(materialsService.updateMaterialChapter)
            const failed = chapterAnchorRef.current
            chapterAnchorRef.current = null
            setFailedAnchorChapterId(failed?.chapter?.id ?? null)
        }
    )

    // Study-program mode: anchor the analysed material on the pinned chapter.
    // Creating the activity sends `chapter_id` and the backend syncs from it,
    // but the parcours buttons generate from `material_id` alone and the
    // generated exercises inherit the source material's chapter — the one the
    // analyse guessed, which the tutor never saw and cannot correct here.
    useEffect(() => {
        if (!useStudyProgram) return
        if (analysisStatus !== "done") return
        // The chapter route is gated by manage_live_sessions, which neither the
        // student (photo-scan flow) nor the parent acting on their token has —
        // firing it there would only buy a 403. Parent mode is unreachable today
        // only because `UserResource` doesn't expose `has_study_program`, which
        // is a resource shape, not an invariant.
        if (selfCreate || props.isParentMode) return
        const materialId = materialFromImage?.id
        const programChapter = watchedStudyProgramChapter
        if (!materialId || !programChapter?.id) return
        if (Number(materialFromImage?.chapter_id) === Number(programChapter.id)) {
            return
        }
        // This exact chapter already failed. Retrying it on every render is the
        // loop this guard exists to prevent; pinning another one is what gets a
        // fresh attempt, and the banner keeps naming the failed one meanwhile.
        if (Number(failedAnchorChapterId) === Number(programChapter.id)) return
        // One in flight already. The success handler moves `chapter_id`, which
        // re-runs this effect, and the comparison above then re-fires only if
        // the tutor pinned a different chapter meanwhile.
        if (chapterAnchorRef.current) return

        chapterAnchorRef.current = { materialId, chapter: programChapter }
        props.apiRequest(
            materialsService.updateMaterialChapter,
            { id: materialId },
            { chapter_id: programChapter.id }
        )
    }, [
        useStudyProgram,
        analysisStatus,
        selfCreate,
        props.isParentMode,
        failedAnchorChapterId,
        materialFromImage?.id,
        materialFromImage?.chapter_id,
        watchedStudyProgramChapter,
    ])

    // Handler for parcours generation from the image flow (ProgressiveAnalysisDisplay).
    // level: '1' (Révision), '2' (Entraînement), '3' (Approfondissement)
    // duration: '5' | '10' | '15' (minutes)
    // options: { instructions, studentInterests }
    // Tracks the selectedLevel for the result header/badge.
    const [imageFlowLevel, setImageFlowLevel] = useState(null)

    const handleGenerateParcours = (level = "2", duration = "5", options = {}) => {
        if (!materialFromImage?.id) {
            console.error("No material found for parcours generation")
            return
        }

        const { instructions, studentInterests } = options
        const targetLevel = level ? parseInt(level, 10) : null
        const targetDuration = duration ? parseInt(duration, 10) : null

        // Track for result view (title + activityLevel badge)
        setImageFlowLevel(level)

        // Transition to results view
        setStep("results")

        // Base request data with optional instructions, student_interests, target_level, duration
        const baseRequestData = {
            ...(targetLevel && { target_level: targetLevel }),
            ...(targetDuration && { duration: targetDuration }),
            ...(instructions && { instructions }),
            ...(studentInterests && { student_interests: studentInterests }),
            ...(historyStudentIds.length > 0 && { student_ids: historyStudentIds }),
        }

        const requestData = {
            material_id: materialFromImage.id,
            ...baseRequestData,
        }

        if (level === "1") {
            // Révision du cours: concept_review, method_review, same_difficulty (simple)
            setMaterials([
                { id: 1, status: "pending", variation: "concept_review" },
                { id: 2, status: "pending", variation: "method_review" },
                { id: 3, status: "pending", variation: "same_difficulty" },
            ])

            props.apiRequest(
                activitiesService.generatePropositionConceptReview,
                { materialId: materialFromImage.id },
                { num_clusters: 1, ...baseRequestData }
            )
            props.apiRequest(
                activitiesService.generatePropositionMethodReview,
                { materialId: materialFromImage.id },
                { num_clusters: 1, ...baseRequestData }
            )
            props.apiRequest(
                activitiesService.generateSimilarExercisesSameDifficulty,
                {},
                requestData
            )
        } else {
            // Entraînement (2) & Approfondissement (3):
            // énoncé fidèle | autre notion | énoncé fidèle diversifié (exercise_type/context)
            // All three generated at the tutor-selected target_level.
            setMaterials([
                { id: 1, status: "pending", variation: "same_difficulty" },
                { id: 2, status: "pending", variation: "other_concepts" },
                { id: 3, status: "pending", variation: "same_difficulty_diversified" },
            ])

            props.apiRequest(
                activitiesService.generateSimilarExercisesSameDifficulty,
                {},
                requestData
            )
            props.apiRequest(
                activitiesService.generateSimilarExercisesOtherConcepts,
                {},
                requestData
            )
            props.apiRequest(
                activitiesService.generateSimilarExercisesSameDifficultyDiversified,
                {},
                requestData
            )
        }
    }

    // Render selected student avatars with deck effect (overlapping)
    const renderStudentAvatars = () => {
        if (!forStudents || selectedStudentIds.length === 0) return null

        const selectedStudents = studentsList?.filter(s => selectedStudentIds.includes(s.id)) || []
        const visibleStudents = selectedStudents.slice(0, 3)
        const remainingCount = selectedStudents.length - 3

        return (
            <TouchableOpacity
                onPress={() => setShowStudentSelection(true)}
                style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginLeft: 10,
                }}
            >
                {visibleStudents.map((student, index) => (
                    <AvatarM
                        key={student.id}
                        size={32}
                        user={student}
                        style={{
                            borderRadius: 16,
                            borderRightWidth: 3,
                            borderColor: theme.colors.white,
                            marginLeft: index !== 0 ? -12 : 0,
                            zIndex: visibleStudents.length - index,
                        }}
                    />
                ))}
                {remainingCount > 0 && (
                    <View
                        style={{
                            width: 32,
                            height: 32,
                            borderRadius: 16,
                            backgroundColor: theme.colors.primaryLighter,
                            borderWidth: 2,
                            borderColor: theme.colors.white,
                            alignItems: "center",
                            justifyContent: "center",
                            marginLeft: -12,
                            zIndex: 0,
                        }}
                    >
                        <TextM
                            fontSize={10}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                        >
                            +{remainingCount}
                        </TextM>
                    </View>
                )}
            </TouchableOpacity>
        )
    }

    const renderFormStep = () => (
        // keyboardShouldPersistTaps="handled" so taps on the ChapterSearchDropdown
        // rows reach their TouchableOpacity instead of being eaten by the
        // ScrollView's keyboard-dismiss when the search input is focused.
        <ScrollView
            style={{}}
            ref={formScrollViewRef}
            keyboardShouldPersistTaps="handled"
        >
            <View style={{}}>
                {/* Header */}
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        position: "relative",
                        marginBottom: 10,
                        overflow: "hidden",
                    }}
                >
                    <ChampionImageM
                        name="sam"
                        variant="main"
                        eye
                        width={200}
                        style={{
                            position: "absolute",
                            right: -30,
                            top: -70,
                            opacity: 0.08,
                            pointerEvents: "none",
                        }}
                    />
                    <View
                        style={{ flexDirection: "row", alignItems: "center" }}
                    >
                        <IconButtonM
                            icon={imageMode ? "arrow-left" : "times"}
                            iconColor={theme.colors.primary}
                            containerColor={theme.colors.white}
                            style={{}}
                            onPress={imageMode ? () => {
                                lastStartedUploadRef.current = null
                                setImageMode(false)
                                setUploadedImage(null)
                                setMaterialFromImage(null)
                                setCreatingActivity(false)
                                setActivityCreated(false)
                                setAnalysisError(null)
                                setAnalysisStatus('idle')
                                setAnalysisSuggestion(null)
                                setImageFlowQuizEnabled(false)
                                setImageFlowQuizError(false)
                                setShowCustomParcours(false)
                                setCustomLevel("2")
                                setCustomInstructions("")
                            } : onClose}
                            size={28}
                        />
                        <TextM
                            fontSize={18}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                        >
                            {imageMode ? "Depuis un fichier..." : forStudents ? "Activité commune" : "Générer une activité"}
                        </TextM>
                        {renderStudentAvatars()}
                    </View>
                    {imageMode ? (
                        <FilledButtonM
                            onPress={onClose}
                            label="Terminé"
                            style={{
                                marginRight: 10,
                                width: null,
                                alignSelf: "center",
                            }}
                            mode={"text"}
                            labelStyle={{ fontSize: 12 }}
                            contentStyle={{
                                height: 30,
                            }}
                        />
                    ) : !props.isParentMode && !selfCreate && !embeddedAsForm && (
                        // "Ancienne interface" is tutor-facing — hidden for
                        // students (self-create) and parents, and for callers
                        // that embed the form as a sub-step (Sam regenerate).
                        <FilledButtonM
                            onPress={() => {
                                onClose()
                                navigation.push("new-todo", {
                                    studentId: forStudents ? null : student?.id,
                                    forStudents: forStudents,
                                })
                            }}
                            label={`Ancienne interface`}
                            style={{
                                marginRight: 10,
                                width: null,
                                alignSelf: "center",
                            }}
                            mode={"text"}
                            labelStyle={{ fontSize: 10 }}
                            contentStyle={{
                                height: 25,
                            }}
                        />
                    )}
                </View>
                <View style={{ padding: 20 }}>
                    {(() => {
                        // The MaterialGenerationForm is rendered in BOTH paths
                        // (with and without image). When an image is dropped we
                        // overlay a non-blocking AI suggestion chip under the
                        // chapter dropdown — the form stays fully interactive
                        // and the tutor can ignore the suggestion entirely.
                        const formNode = (
                            <MaterialGenerationForm
                                control={control}
                                isParentMode={props.isParentMode}
                                hideClassSubject={!imageMode}
                                hideTypeAndInstructions={imageMode}
                                onDurationPicked={onDurationPicked}
                                forceShowType={
                                    imageMode &&
                                    (analysisStatus === 'error' ||
                                        (analysisStatus === 'done' &&
                                            !analysisSuggestion?.level &&
                                            !materialFromImage?.level))
                                }
                                classOptions={classOptions}
                                subjectOptions={subjectOptions}
                                levelOptions={levelOptions}
                                chaptersOptions={chaptersOptions}
                                chapters={chapters}
                                useStudyProgram={useStudyProgram}
                                setUseStudyProgram={handleSetUseStudyProgram}
                                watchedClass={watchedClass}
                                watchedSubject={watchedSubject}
                                watchedInstructions={watchedInstructions}
                                watchedChapter={watchedChapter}
                                watchedStudyProgramChapter={watchedStudyProgramChapter}
                                setShowChapterSelection={setShowChapterSelection}
                                hasStudyProgram={
                                    forStudents
                                        ? studentsList
                                              ?.filter((x) => selectedStudentIds?.includes(x?.id))
                                              ?.some((x) => x?.has_study_program)
                                        : student?.has_study_program
                                }
                                getChaptersRequest={props.getChaptersRequest}
                                isFormValid={isFormValid}
                                setValue={setValue}
                                onSubmit={handleSubmit(onSubmit)}
                                studentInterests={!forStudents ? student?.profile?.interests : null}
                                personalizeWithInterests={personalizeWithInterests}
                                setPersonalizeWithInterests={setPersonalizeWithInterests}
                                priorityChapterIds={priorityChapterIds}
                                priorityBadge={priorityBadge}
                                onGlobalChapterSelect={handleGlobalChapterSelect}
                                currentSubjectName={currentSubjectName}
                                currentSubjectId={watchedSubject || null}
                                aiSuggestions={
                                    imageMode &&
                                    analysisStatus === 'done' &&
                                    analysisSuggestion?.confidence === 'medium'
                                        ? {
                                              suggestion: analysisSuggestion.suggestion,
                                              alternatives: analysisSuggestion.alternatives,
                                          }
                                        : null
                                }
                                chapterSuggestionSlot={
                                    imageMode ? (
                                        <View>
                                            {/* `initialImage` opens straight into image mode, so
                                                the drop zone that normally carries this
                                                explanation is never rendered — without it the
                                                tutor faces a file that isn't being analysed and
                                                nothing tying it to the picker above. */}
                                            {useStudyProgram && !watchedStudyProgramChapter && (
                                                <TextM
                                                    fontSize={11}
                                                    color={theme.colors.primary}
                                                    wrap
                                                    style={{ marginTop: 4, marginBottom: 8 }}
                                                >
                                                    Choisis un chapitre du programme pour continuer
                                                    — ou désactive le mode programme.
                                                </TextM>
                                            )}
                                            {/* Only while the chapter that failed is still the
                                                pinned one: picking another re-arms the effect and
                                                takes this down on its own. */}
                                            {useStudyProgram &&
                                                failedAnchorChapterId &&
                                                Number(failedAnchorChapterId) ===
                                                    Number(watchedStudyProgramChapter?.id) && (
                                                <TextM
                                                    fontSize={11}
                                                    color={theme.colors.error}
                                                    wrap
                                                    style={{ marginTop: 4, marginBottom: 8 }}
                                                >
                                                    Le chapitre du programme n'a pas pu être
                                                    appliqué à ce fichier : les exercices
                                                    similaires suivraient le chapitre détecté par
                                                    l'analyse. Créer l'activité reste correct.
                                                </TextM>
                                            )}
                                            {analysisStatus === 'analyzing' && !watchedChapter && (
                                                <View style={{
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                    marginTop: 4,
                                                    marginBottom: 8,
                                                }}>
                                                    <ActivityIndicator
                                                        size={12}
                                                        color={theme.colors.primary}
                                                        style={{ marginRight: 6 }}
                                                    />
                                                    <TextM fontSize={11} color={theme.colors.greyAlt}>
                                                        Suggestion en cours…
                                                    </TextM>
                                                </View>
                                            )}
                                            {/* A silent class/subject
                                                correction (adjacent-class match, or "Basculer")
                                                re-triggers the [watchedClass, watchedSubject]
                                                chapters refetch for the corrected scope.
                                                `selectedChapter` itself gets re-applied by
                                                `pendingChapterIdRef` as soon as that fetch starts
                                                resolving, but `chapters` (this corrected scope's
                                                list — the one the AI matched the chapter against
                                                isn't the same list this dropdown fetches, since the
                                                dropdown fetches the chosen class only, not the
                                                widened adjacent-class pool) only contains the matching
                                                entry once ITS OWN fetch resolves. Until then the
                                                dropdown has an id to show but no name to show it
                                                with — a blank-looking field the tutor would otherwise
                                                read as "nothing happened" for ~2-3s. */}
                                            {analysisStatus === 'done' &&
                                                analysisSuggestion?.confidence === 'high' &&
                                                analysisSuggestion?.suggestion?.id &&
                                                !!watchedChapter &&
                                                !chapters?.some(
                                                    (c) => String(c.id) === String(watchedChapter)
                                                ) && (
                                                    <View style={{
                                                        flexDirection: 'row',
                                                        alignItems: 'center',
                                                        marginTop: 4,
                                                        marginBottom: 8,
                                                    }}>
                                                        <ActivityIndicator
                                                            size={12}
                                                            color={theme.colors.primary}
                                                            style={{ marginRight: 6 }}
                                                        />
                                                        <TextM fontSize={11} color={theme.colors.greyAlt}>
                                                            Mise à jour du chapitre…
                                                        </TextM>
                                                    </View>
                                                )}
                                            {analysisStatus === 'error' && analysisError && !watchedChapter && (
                                                <TextM
                                                    fontSize={11}
                                                    color={theme.colors.error}
                                                    wrap
                                                    style={{ marginTop: 4, marginBottom: 8 }}
                                                >
                                                    {useStudyProgram
                                                        ? // No chapter dropdown here — the program chapter
                                                          // already answers the question the suggestion
                                                          // would have answered.
                                                          "Suggestion AI indisponible. Le chapitre du programme est conservé."
                                                        : texts.suggestionUnavailable}
                                                </TextM>
                                            )}
                                            {/* High-confidence auto-pre-select: show a discreet
                                                "Sélectionné par Sam" line above the chapter chip.
                                                The chip itself is rendered by ChapterSearchDropdown
                                                — clicking ✕ there clears the selection so this
                                                indicator disappears with it. Gated on the chapter
                                                being resolvable in `chapters` (same check as the
                                                loader above) so the two never overlap — the tutor
                                                sees the loader, then this, never both at once. */}
                                            {analysisStatus === 'done' &&
                                                analysisSuggestion?.confidence === 'high' &&
                                                watchedChapter &&
                                                analysisSuggestion?.suggestion?.id?.toString() ===
                                                    watchedChapter?.toString() &&
                                                chapters?.some(
                                                    (c) => String(c.id) === String(watchedChapter)
                                                ) && (
                                                    <View
                                                        style={{
                                                            flexDirection: 'row',
                                                            alignItems: 'center',
                                                            marginTop: 4,
                                                            marginBottom: 8,
                                                        }}
                                                    >
                                                        <FontAwesome5
                                                            name="magic"
                                                            size={11}
                                                            color={theme.colors.primary}
                                                            style={{ marginRight: 6 }}
                                                        />
                                                        <TextM
                                                            fontSize={11}
                                                            color={theme.colors.greyAlt}
                                                        >
                                                            Sélectionné par Sam
                                                        </TextM>
                                                    </View>
                                                )}
                                            {/* Done + no suggestion: always feedback the OCR title
                                                so the tutor knows Sam read the image, even when no
                                                chapter matched in the current class+subject scope.
                                                Pure informational chip — no action; the tutor uses
                                                the chapter dropdown manually. The cross-class chip
                                                below offers the auto-switch when applicable. */}
                                            {/* De-emphasized on purpose: this line is pure feedback
                                                (no action attached to it), so it stays a quiet caption
                                                rather than competing visually with the actionable
                                                suggestion card below it. */}
                                            {analysisStatus === 'done' &&
                                                !analysisSuggestion?.suggestion?.id &&
                                                analysisSuggestion?.title &&
                                                !watchedChapter && (
                                                    <View style={{ marginTop: 4, marginBottom: 6 }}>
                                                        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
                                                            <FontAwesome5
                                                                name="magic"
                                                                size={10}
                                                                color={theme.colors.greyAlt}
                                                                style={{ marginRight: 6, marginTop: 2 }}
                                                            />
                                                            <TextM
                                                                fontSize={11}
                                                                color={theme.colors.greyAlt}
                                                                wrap
                                                                style={{ flex: 1 }}
                                                            >
                                                                Sam a lu « {analysisSuggestion.title} » — pas de correspondance en {watchedClass}
                                                                {currentSubjectName ? ` · ${currentSubjectName}` : ''}
                                                                {useStudyProgram
                                                                    ? // The program chapter already answers it.
                                                                      " — le chapitre du programme est conservé."
                                                                    : ` ${texts.pickChapter}`}
                                                            </TextM>
                                                        </View>
                                                    </View>
                                                )}
                                            {/* Scope-mismatch suggestion card:
                                                the one actionable moment in the group, so it carries
                                                the visual weight — accent bar + eyebrow badge + a real
                                                pill button. No chapter name here on purpose — the
                                                detection is subject only (volet 2); "Basculer" re-runs
                                                the chapter search in the new subject via
                                                reanalyzeScope() (handleScopeSwitchAccept above). Class
                                                mismatches are handled separately and silently, server-side
                                                (adjacent-class chapter search), no chip needed. */}
                                            {analysisStatus === 'done' &&
                                                !watchedChapter &&
                                                !crossClassDismissed &&
                                                crossClassSuggestion && (
                                                    <View
                                                        style={{
                                                            flexDirection: 'row',
                                                            backgroundColor: theme.colors.primaryLighter,
                                                            borderRadius: 10,
                                                            marginBottom: 8,
                                                            overflow: 'hidden',
                                                        }}
                                                    >
                                                        <View style={{ width: 4, backgroundColor: theme.colors.primary }} />
                                                        <View style={{ flex: 1, paddingVertical: 10, paddingHorizontal: 12 }}>
                                                            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 4 }}>
                                                                <FontAwesome5
                                                                    name="magic"
                                                                    size={10}
                                                                    color={theme.colors.primary}
                                                                    style={{ marginRight: 6 }}
                                                                />
                                                                <TextM
                                                                    fontSize={10}
                                                                    fontWeight="Bold"
                                                                    color={theme.colors.primary}
                                                                    style={{ textTransform: 'uppercase', letterSpacing: 0.4 }}
                                                                >
                                                                    Suggestion de Sam
                                                                </TextM>
                                                            </View>
                                                            <TextM fontSize={13} fontWeight="Bold" color={theme.colors.primary} wrap>
                                                                Ça ressemble à {crossClassSuggestion.school_subject_name
                                                                    ? subjectWithPartitive(crossClassSuggestion.school_subject_name)
                                                                    : 'une autre matière'}
                                                            </TextM>
                                                            <View style={{
                                                                flexDirection: 'row',
                                                                justifyContent: 'flex-end',
                                                                alignItems: 'center',
                                                                marginTop: 10,
                                                            }}>
                                                                <TouchableOpacity
                                                                    disabled={crossClassLoading}
                                                                    onPress={() => {
                                                                        amplitude.track('material_scope_suggestion.dismissed')
                                                                        setCrossClassDismissed(true)
                                                                    }}
                                                                    style={{
                                                                        paddingVertical: 8,
                                                                        paddingHorizontal: 12,
                                                                    }}
                                                                >
                                                                    <TextM
                                                                        fontSize={12}
                                                                        color={theme.colors.greyAlt}
                                                                    >
                                                                        Ignorer
                                                                    </TextM>
                                                                </TouchableOpacity>
                                                                <TouchableOpacity
                                                                    disabled={crossClassLoading}
                                                                    onPress={handleScopeSwitchAccept}
                                                                    style={{
                                                                        flexDirection: 'row',
                                                                        alignItems: 'center',
                                                                        paddingVertical: 8,
                                                                        paddingHorizontal: 18,
                                                                        backgroundColor: theme.colors.primary,
                                                                        borderRadius: 999,
                                                                        marginLeft: 4,
                                                                        opacity: crossClassLoading ? 0.6 : 1,
                                                                    }}
                                                                >
                                                                    {crossClassLoading && (
                                                                        <ActivityIndicator
                                                                            size={10}
                                                                            color={theme.colors.white}
                                                                            style={{ marginRight: 6 }}
                                                                        />
                                                                    )}
                                                                    <TextM
                                                                        fontSize={12}
                                                                        fontWeight="Bold"
                                                                        color={theme.colors.white}
                                                                    >
                                                                        Basculer
                                                                    </TextM>
                                                                </TouchableOpacity>
                                                            </View>
                                                        </View>
                                                    </View>
                                                )}
                                        </View>
                                    ) : null
                                }
                                skillPartsSection={
                                    activeChapterSkillParts.length > 0 ? (
                                        <SkillPartsChipPicker
                                            skillParts={activeChapterSkillParts}
                                            selectedPositions={selectedSkillPartPositions}
                                            onTogglePosition={toggleSkillPartPosition}
                                        />
                                    ) : null
                                }
                                primaryActionOverride={
                                    imageMode && uploadedImage ? (
                                        <CreateActivityButton
                                            onPress={() =>
                                                handleCreateActivityFromImage(
                                                    imageFlowQuizEnabled || false
                                                )
                                            }
                                            loading={
                                                creatingActivity ||
                                                analysisStatus === "analyzing"
                                            }
                                            isCreated={activityCreated}
                                            disabled={
                                                imageFlowChapterMissing ||
                                                !materialFromImage?.id ||
                                                analysisStatus === "analyzing" ||
                                                creatingActivity ||
                                                activityCreated
                                            }
                                            loadingLabel={
                                                creatingActivity
                                                    ? texts.composing
                                                    : "Analyse en cours…"
                                            }
                                            createdLabel="Activité créée"
                                        />
                                    ) : null
                                }
                                secondaryActionsSlot={
                                    imageMode &&
                                    uploadedImage &&
                                    analysisStatus !== "error" ? (
                                        <View style={{ marginTop: 20 }}>
                                            {/* Separator with centered label */}
                                            <View
                                                style={{
                                                    flexDirection: "row",
                                                    alignItems: "center",
                                                    marginBottom: 14,
                                                }}
                                            >
                                                <View
                                                    style={{
                                                        flex: 1,
                                                        height: 1,
                                                        backgroundColor: theme.colors.lightMore,
                                                    }}
                                                />
                                                <TextM
                                                    fontSize={12}
                                                    color={theme.colors.grey}
                                                    style={{ marginHorizontal: 12 }}
                                                >
                                                    ou générer des activités similaires
                                                </TextM>
                                                <View
                                                    style={{
                                                        flex: 1,
                                                        height: 1,
                                                        backgroundColor: theme.colors.lightMore,
                                                    }}
                                                />
                                            </View>

                                            {(() => {
                                                // Waiting on the anchor too: "done"
                                                // and the anchor PATCH land in the
                                                // same pass, so a click inside that
                                                // round-trip would generate from the
                                                // pre-anchor chapter. "Créer
                                                // l'activité" sends chapter_id itself
                                                // and needs no such gate.
                                                const parcoursDisabled =
                                                    imageFlowChapterMissing ||
                                                    creatingActivity ||
                                                    !materialFromImage?.id ||
                                                    analysisStatus === "analyzing" ||
                                                    props.updateChapterRequest
                                                const parcoursDuration =
                                                    watch("selectedDuration") || "5"
                                                const renderQuickButton = (
                                                    onPress,
                                                    icon,
                                                    label
                                                ) => (
                                                    <TouchableRippleM
                                                        onPress={onPress}
                                                        disabled={parcoursDisabled}
                                                        style={{
                                                            flex: 1,
                                                            borderRadius: 12,
                                                            overflow: "hidden",
                                                            borderWidth: 2,
                                                            borderColor:
                                                                theme.colors.primary,
                                                            opacity: parcoursDisabled
                                                                ? 0.5
                                                                : 1,
                                                        }}
                                                    >
                                                        <View
                                                            style={{
                                                                paddingVertical: 12,
                                                                paddingHorizontal: 8,
                                                                alignItems: "center",
                                                                flexDirection: "row",
                                                                justifyContent: "center",
                                                                backgroundColor:
                                                                    theme.colors.white,
                                                            }}
                                                        >
                                                            <FontAwesome5
                                                                name={icon}
                                                                size={12}
                                                                color={
                                                                    theme.colors.primary
                                                                }
                                                                style={{
                                                                    marginRight: 6,
                                                                }}
                                                            />
                                                            <TextM
                                                                fontSize={13}
                                                                fontWeight="Bold"
                                                                color={
                                                                    theme.colors.primary
                                                                }
                                                                numberOfLines={1}
                                                            >
                                                                {label}
                                                            </TextM>
                                                        </View>
                                                    </TouchableRippleM>
                                                )
                                                return (
                                                    <>
                                                        <View
                                                            style={{
                                                                flexDirection: "row",
                                                                gap: 10,
                                                            }}
                                                        >
                                                            {renderQuickButton(
                                                                () =>
                                                                    handleGenerateParcours(
                                                                        "1",
                                                                        parcoursDuration
                                                                    ),
                                                                "redo",
                                                                "Réviser"
                                                            )}
                                                            {renderQuickButton(
                                                                () =>
                                                                    handleGenerateParcours(
                                                                        "2",
                                                                        parcoursDuration
                                                                    ),
                                                                "dumbbell",
                                                                "S'entraîner"
                                                            )}
                                                        </View>

                                                        <TouchableOpacity
                                                            onPress={() =>
                                                                setShowCustomParcours(
                                                                    (v) => !v
                                                                )
                                                            }
                                                            disabled={parcoursDisabled}
                                                            activeOpacity={0.6}
                                                            style={{
                                                                alignSelf: "center",
                                                                paddingVertical: 10,
                                                                paddingHorizontal: 14,
                                                                marginTop: 4,
                                                                opacity: parcoursDisabled
                                                                    ? 0.5
                                                                    : 1,
                                                            }}
                                                        >
                                                            <TextM
                                                                fontSize={13}
                                                                fontWeight="Medium"
                                                                color={
                                                                    theme.colors.primary
                                                                }
                                                                style={{
                                                                    textDecorationLine:
                                                                        "underline",
                                                                }}
                                                            >
                                                                {showCustomParcours
                                                                    ? "Masquer la personnalisation"
                                                                    : "Personnaliser"}
                                                            </TextM>
                                                        </TouchableOpacity>

                                                        {showCustomParcours && (
                                                            <View
                                                                style={{
                                                                    marginTop: 14,
                                                                    padding: 14,
                                                                    backgroundColor:
                                                                        theme.colors.lighter,
                                                                    borderRadius: 12,
                                                                    borderWidth: 1,
                                                                    borderColor:
                                                                        theme.colors.lightMore,
                                                                }}
                                                            >
                                                                {/* Type selector */}
                                                                <View style={{ marginBottom: 12 }}>
                                                                    <SelectInputM
                                                                        label="Type"
                                                                        options={levelOptions}
                                                                        value={customLevel}
                                                                        onSelect={setCustomLevel}
                                                                    />
                                                                </View>

                                                                {/* Instructions */}
                                                                <View style={{ marginBottom: 12 }}>
                                                                    <View
                                                                        style={{
                                                                            flexDirection: "row",
                                                                            alignItems: "center",
                                                                            justifyContent: "space-between",
                                                                            marginBottom: 5,
                                                                        }}
                                                                    >
                                                                        <TextM
                                                                            color={theme.colors.primary}
                                                                            fontWeight="Medium"
                                                                            fontSize={12}
                                                                        >
                                                                            Instructions
                                                                        </TextM>
                                                                        {!forStudents && student?.profile?.interests && (
                                                                            <TouchableOpacity
                                                                                onPress={() =>
                                                                                    setPersonalizeWithInterests(
                                                                                        !personalizeWithInterests
                                                                                    )
                                                                                }
                                                                                activeOpacity={0.7}
                                                                                style={{
                                                                                    flexDirection: "row",
                                                                                    alignItems: "center",
                                                                                    paddingVertical: 5,
                                                                                    paddingHorizontal: 10,
                                                                                    borderRadius: 14,
                                                                                    backgroundColor: personalizeWithInterests
                                                                                        ? theme.colors.primary
                                                                                        : theme.colors.white,
                                                                                    borderWidth: 1,
                                                                                    borderColor: personalizeWithInterests
                                                                                        ? theme.colors.primary
                                                                                        : theme.colors.lightMore,
                                                                                }}
                                                                            >
                                                                                <FontAwesome5
                                                                                    name={personalizeWithInterests ? "futbol" : "magic"}
                                                                                    size={11}
                                                                                    color={personalizeWithInterests ? theme.colors.white : theme.colors.grey}
                                                                                    style={{ marginRight: 6 }}
                                                                                />
                                                                                <TextM
                                                                                    color={personalizeWithInterests ? theme.colors.white : theme.colors.grey}
                                                                                    fontSize={11}
                                                                                    fontWeight="Medium"
                                                                                >
                                                                                    {personalizeWithInterests
                                                                                        ? student.profile.interests
                                                                                        : "Personnaliser pour intérêts"}
                                                                                </TextM>
                                                                                {personalizeWithInterests && (
                                                                                    <FontAwesome5
                                                                                        name="times"
                                                                                        size={10}
                                                                                        color={theme.colors.white}
                                                                                        style={{ marginLeft: 6 }}
                                                                                    />
                                                                                )}
                                                                            </TouchableOpacity>
                                                                        )}
                                                                    </View>
                                                                    <UncontrolledTextInputM
                                                                        value={customInstructions}
                                                                        onChangeText={setCustomInstructions}
                                                                        multiline
                                                                        numberOfLines={2}
                                                                        placeholder={texts.customInstructions}
                                                                        mode="outlined"
                                                                        outlineColor={theme.colors.lightMore}
                                                                        activeOutlineColor={theme.colors.primary}
                                                                        style={{
                                                                            backgroundColor: theme.colors.white,
                                                                            minHeight: 80,
                                                                        }}
                                                                    />
                                                                </View>

                                                                {/* Generate + Cancel */}
                                                                <View
                                                                    style={{
                                                                        flexDirection: "row",
                                                                        gap: 10,
                                                                    }}
                                                                >
                                                                    <TouchableRippleM
                                                                        onPress={() => {
                                                                            handleGenerateParcours(
                                                                                customLevel,
                                                                                parcoursDuration,
                                                                                {
                                                                                    instructions:
                                                                                        customInstructions?.trim() || undefined,
                                                                                    studentInterests:
                                                                                        personalizeWithInterests &&
                                                                                        !forStudents
                                                                                            ? student?.profile?.interests
                                                                                            : null,
                                                                                }
                                                                            )
                                                                        }}
                                                                        disabled={parcoursDisabled}
                                                                        style={{
                                                                            flex: 1,
                                                                            borderRadius: 12,
                                                                            overflow: "hidden",
                                                                            opacity: parcoursDisabled ? 0.5 : 1,
                                                                        }}
                                                                    >
                                                                        <LinearGradient
                                                                            colors={[
                                                                                theme.colors.samLight,
                                                                                theme.colors.purple,
                                                                            ]}
                                                                            start={{ x: 0, y: 0 }}
                                                                            end={{ x: 1, y: 1 }}
                                                                            style={{
                                                                                paddingVertical: 12,
                                                                                paddingHorizontal: 16,
                                                                                alignItems: "center",
                                                                            }}
                                                                        >
                                                                            <TextM
                                                                                fontSize={14}
                                                                                fontWeight="Bold"
                                                                                color={theme.colors.white}
                                                                            >
                                                                                Générer
                                                                            </TextM>
                                                                        </LinearGradient>
                                                                    </TouchableRippleM>
                                                                    <IconButtonM
                                                                        icon="times"
                                                                        iconColor={theme.colors.grey}
                                                                        size={22}
                                                                        onPress={() => {
                                                                            setShowCustomParcours(false)
                                                                            setCustomLevel("2")
                                                                            setCustomInstructions("")
                                                                        }}
                                                                        style={{
                                                                            margin: 0,
                                                                            alignSelf: "center",
                                                                        }}
                                                                    />
                                                                </View>
                                                            </View>
                                                        )}
                                                    </>
                                                )
                                            })()}
                                        </View>
                                    ) : null
                                }
                            />
                        )

                        if (imageMode && uploadedImage) {
                            return (
                                <View style={{
                                    flexDirection: isSmallScreen ? "column" : "row",
                                    gap: 0,
                                    margin: -20,
                                }}>
                                    <View style={{
                                        flex: isSmallScreen ? undefined : 1,
                                        width: isSmallScreen ? "100%" : "50%",
                                        backgroundColor: theme.colors.lighter,
                                        padding: uploadedImage?.mimeType === "application/pdf" ? 0 : 20,
                                    }}>
                                        <ImageUploadZone
                                            uploadedImage={uploadedImage}
                                            fullHeight={!isSmallScreen}
                                            noBorder
                                        />
                                    </View>
                                    <View style={{
                                        flex: isSmallScreen ? undefined : 1,
                                        width: isSmallScreen ? "100%" : "50%",
                                        paddingHorizontal: 20,
                                        paddingTop: isSmallScreen ? 20 : 0,
                                        paddingBottom: 20,
                                    }}>
                                        {formNode}
                                    </View>
                                </View>
                            )
                        }

                        // No-image flow: lift Class + Matière out of the form
                        // into a modal-level "scope header" so the scope is
                        // shared with the drop zone below. Same Controllers /
                        // SearchableSelectM as the form had originally — react-
                        // hook-form sees them as the same fields via `control`.
                        //
                        // Hidden in study-program mode: there the scope comes
                        // from the picked program chapter (which already shows
                        // its matière + classe on its card), so asking for them
                        // again only let the two contradict each other.
                        return (
                            <>
                                {!useStudyProgram && (
                                <View style={{ flexDirection: "row", marginBottom: 15, zIndex: 1100 }}>
                                    <View style={{ flex: 1, marginRight: 10 }}>
                                        <Controller
                                            control={control}
                                            name="selectedClass"
                                            render={({ field: { onChange, value } }) => (
                                                <SearchableSelectM
                                                    label="Classe"
                                                    options={classOptions}
                                                    value={value}
                                                    onSelect={onChange}
                                                    placeholder="Rechercher une classe..."
                                                />
                                            )}
                                        />
                                    </View>
                                    <View style={{ flex: 1, marginLeft: 10 }}>
                                        <Controller
                                            control={control}
                                            name="selectedSubject"
                                            render={({ field: { onChange, value } }) => (
                                                <SearchableSelectM
                                                    label="Matière"
                                                    options={subjectOptions}
                                                    value={value}
                                                    onSelect={onChange}
                                                    placeholder="Rechercher une matière..."
                                                />
                                            )}
                                        />
                                    </View>
                                </View>
                                )}
                                {formNode}
                            </>
                        )
                    })()}

                    {/* Image Upload Zone - at the bottom (no-image mode only).
                        Contextualised with the tutor's current class + subject
                        selection so they understand the upload is scoped, and
                        disabled-with-CTA when either is empty. */}
                    {/* Hidden in student self-create: the student adds an
                        exercise from the platform (a chapter), not from their
                        own files. Only the tutor uploads material. Also hidden
                        when the modal is embedded as a pure form (Sam
                        regenerate), where the upload path has no meaning. */}
                    {!imageMode && !selfCreate && !embeddedAsForm && (
                        <ImageUploadZone
                            uploadedImage={null}
                            separatorPosition="before"
                            disabled={!watchedClass || !watchedSubject}
                            contextLabel={
                                watchedClass && currentSubjectName
                                    ? `${watchedClass} · ${currentSubjectName}`
                                    : null
                            }
                            disabledLabel={
                                // Names what's missing, nothing more: the zone
                                // appends a fixed "pour analyser un fichier"
                                // under it. The way out through the toggle is
                                // spelled out in the inline hint instead.
                                useStudyProgram
                                    ? texts.programChapterMissing
                                    : undefined
                            }
                            isParentMode={props.isParentMode}
                            onTryActivateWhenDisabled={handleDropZoneActivateWhenDisabled}
                            onImageSelect={(file) => {
                                const isPDF = file.mimeType === "application/pdf"

                                if (IS_WEB && !isPDF) {
                                    // On web with image: show crop modal first
                                    setImageToCrop(file)
                                    setShowCropModal(true)
                                } else {
                                    // On mobile or PDF: proceed directly (no crop for PDF)
                                    setUploadedImage(file)
                                    setImageMode(true)
                                }
                            }}
                        />
                    )}
                </View>
            </View>
        </ScrollView>
    )

    const renderResultsStep = () => (
        <View style={{ width: "100%", height: "100%" }}>
            <View style={{ flex: 1 }}>
                {/* Header */}
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        justifyContent: "space-between",
                        position: "relative",
                        marginBottom: 10,
                        overflow: "hidden",
                    }}
                >
                    <ChampionImageM
                        name="sam"
                        variant="main"
                        eye
                        width={200}
                        style={{
                            position: "absolute",
                            right: -30,
                            top: -70,
                            opacity: 0.08,
                            pointerEvents: "none",
                        }}
                    />
                    <View
                        style={{ flexDirection: "row", alignItems: "center", flexShrink: 1 }}
                    >
                        <IconButtonM
                            icon={"arrow-left"}
                            iconColor={theme.colors.primary}
                            containerColor={theme.colors.white}
                            style={{}}
                            onPress={() =>
                                mode === "similar" ? onClose() : setStep("form")
                            }
                            size={28}
                        />
                        <TextM
                            fontSize={isSmallScreen ? 16 : 18}
                            fontWeight="Bold"
                            color={theme.colors.primary}
                            numberOfLines={1}
                            style={{ flexShrink: 1 }}
                        >
                            {mode === "similar"
                                ? selectedLevel === '1'
                                    ? "Exercices pour réviser"
                                    : selectedLevel === '3'
                                    ? "Exercices d'approfondissement"
                                    : "Exercices pour s'entraîner"
                                : imageMode && imageFlowLevel
                                ? imageFlowLevel === '1'
                                    ? "Exercices pour réviser"
                                    : imageFlowLevel === '3'
                                    ? "Exercices d'approfondissement"
                                    : "Exercices pour s'entraîner"
                                : imageMode
                                ? "Parcours d'activités"
                                : "Énoncés générés"}
                        </TextM>
                        {renderStudentAvatars()}
                    </View>
                    <View style={{ flexDirection: "row", alignItems: "center" }}>
                        {canRegenerate &&
                            (isSmallScreen ? (
                                <IconButtonM
                                    icon="sync-alt"
                                    iconColor={theme.colors.primary}
                                    size={18}
                                    style={{ margin: 0 }}
                                    disabled={cardsPending}
                                    onPress={regenerateCards}
                                    accessibilityLabel="Autres énoncés"
                                />
                            ) : (
                                <FilledButtonM
                                    onPress={regenerateCards}
                                    label="Autres énoncés"
                                    icon="sync-alt"
                                    mode="text"
                                    disabled={cardsPending}
                                    style={{ width: null, alignSelf: "center" }}
                                    labelStyle={{ fontSize: 12 }}
                                    contentStyle={{ height: 25 }}
                                />
                            ))}
                        <FilledButtonM
                            onPress={onClose}
                            label={`Terminer`}
                            style={{
                                marginRight: isSmallScreen ? 0 : 10,
                                width: null,
                                alignSelf: "center",
                            }}
                            mode={"text"}
                            labelStyle={{ fontSize: 12 }}
                            contentStyle={{
                                height: 25,
                            }}
                        />
                    </View>
                </View>
                <View style={{ padding: 20, flex: 1 }}>
                    <MaterialResultsCards
                        materials={materials}
                        mode={mode}
                        schoolSubjects={effectiveSchoolSubjects}
                        chaptersOptions={chaptersOptions}
                        watchedSubject={watchedSubject}
                        watchedChapter={watchedChapter}
                        loadingStates={loadingStates}
                        onCreateActivity={createActivity}
                        onReplaceCard={canRegenerate ? replaceCard : undefined}
                        quizErrorStates={quizErrorStates}
                        onClearQuizError={(materialId) => {
                            setQuizErrorStates((prev) => ({ ...prev, [materialId]: false }))
                        }}
                        isPrerequisiteQuizEnabled={isPrerequisiteQuizEnabled}
                        activityLevel={
                            mode === "similar"
                                ? selectedLevel || "2"
                                : imageMode && imageFlowLevel
                                ? imageFlowLevel
                                : watch("selectedLevel")
                        }
                    />
                </View>
            </View>
        </View>
    )

    return (
        <>
            <ModalM
                visible={isOpen}
                onDismiss={onClose}
                style={{
                    maxHeight: "90%",
                    maxWidth: step === "results" ? "90%" : (imageMode && !isSmallScreen ? 900 : 600),
                    width: "90%",
                    overflow: "hidden",
                }}
            >
                {step === "form" ? renderFormStep() : renderResultsStep()}
            </ModalM>

            {/* Study Program Chapter Selection Modal */}
            <StudyProgramChapterSelectionModal
                visible={showChapterSelection}
                onDismiss={() => setShowChapterSelection(false)}
                onSelectChapter={(chapter) => {
                    setValue("studyProgramChapter", chapter)
                    setShowChapterSelection(false)
                }}
                studentId={forStudents ? selectedStudentIds.join(',') : student?.id}
                initialSelectedChapter={watchedStudyProgramChapter}
                title="Sélectionner un chapitre du programme"
            />

            {/* Chapter Selection Modal for Image Flow */}
            <ChapterSelectionModal
                visible={showImageChapterSearch}
                onDismiss={() => setShowImageChapterSearch(false)}
                onSelectChapter={handleImageChapterSelect}
                studentId={resolvedStudent?.id}
                initialSchoolSubjectId={materialFromImage?.school_subject_id}
                initialClass={materialFromImage?.class || resolvedStudent?.profile?.class}
                hasStudyProgram={resolvedStudent?.has_study_program}
                title="Choisir un chapitre"
            />

            {/* Web-only: Image Crop Modal */}
            {IS_WEB && (
                <ImageCropModal
                    visible={showCropModal}
                    imageUri={imageToCrop?.uri}
                    onCancel={() => {
                        setShowCropModal(false)
                        setImageToCrop(null)
                    }}
                    onConfirm={(croppedImage) => {
                        setShowCropModal(false)
                        if (croppedImage) {
                            // Use cropped image
                            setUploadedImage(croppedImage)
                        } else {
                            // User skipped cropping, use original
                            setUploadedImage(imageToCrop)
                        }
                        setImageToCrop(null)
                        setImageMode(true)
                    }}
                />
            )}

            {/* Student Selection Modal */}
            {forStudents && (
                <ModalM
                    visible={showStudentSelection}
                    onDismiss={() => setShowStudentSelection(false)}
                    style={{
                        maxHeight: "80%",
                        maxWidth: 600,
                        width: "90%",
                    }}
                >
                    <View style={{ padding: 20, width: "100%" }}>
                        <View
                            style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 20,
                            }}
                        >
                            <TextM
                                fontSize={18}
                                fontWeight="Bold"
                                color={theme.colors.primary}
                            >
                                Sélectionner les élèves
                            </TextM>
                            <IconButtonM
                                icon="times"
                                iconColor={theme.colors.primary}
                                onPress={() => setShowStudentSelection(false)}
                                size={24}
                            />
                        </View>

                        <ScrollView style={{ maxHeight: 400, width: "100%" }}>
                            {studentsList?.map((student) => {
                                const isSelected = selectedStudentIds.includes(student.id)
                                return (
                                    <TouchableRippleM
                                        key={student.id}
                                        onPress={() => {
                                            setSelectedStudentIds((prev) =>
                                                isSelected
                                                    ? prev.filter((id) => id !== student.id)
                                                    : [...prev, student.id]
                                            )
                                        }}
                                        style={{
                                            flexDirection: "row",
                                            alignItems: "center",
                                            padding: 12,
                                            marginBottom: 8,
                                            borderRadius: 8,
                                            backgroundColor: isSelected
                                                ? theme.colors.primaryLighter
                                                : theme.colors.lighter,
                                            borderWidth: 2,
                                            borderColor: isSelected
                                                ? theme.colors.primary
                                                : theme.colors.lightMore,
                                        }}
                                    >
                                        <AvatarM user={student} size={40} />
                                        <TextM
                                            fontSize={14}
                                            fontWeight={isSelected ? "Bold" : "Regular"}
                                            color={theme.colors.primary}
                                            style={{ marginLeft: 12, flex: 1 }}
                                        >
                                            {student.display_name}
                                        </TextM>
                                        <View
                                            style={{
                                                width: 24,
                                                height: 24,
                                                borderRadius: 12,
                                                backgroundColor: isSelected
                                                    ? theme.colors.primary
                                                    : theme.colors.white,
                                                alignItems: "center",
                                                justifyContent: "center",
                                                borderWidth: isSelected ? 0 : 2,
                                                borderColor: theme.colors.lightMore,
                                                marginLeft: 12,
                                            }}
                                        >
                                            {isSelected && (
                                                <FontAwesome5
                                                    name="check"
                                                    size={12}
                                                    color={theme.colors.white}
                                                />
                                            )}
                                        </View>
                                    </TouchableRippleM>
                                )
                            })}
                        </ScrollView>

                        <View
                            style={{
                                flexDirection: "row",
                                justifyContent: "space-between",
                                marginTop: 20,
                                gap: 10,
                            }}
                        >
                            <FilledButtonM
                                onPress={() => {
                                    setSelectedStudentIds(studentsList?.map((s) => s.id) || [])
                                }}
                                label="Tout sélectionner"
                                mode="outlined"
                                style={{ flex: 1 }}
                            />
                            <FilledButtonM
                                onPress={() => setShowStudentSelection(false)}
                                label={`Confirmer (${selectedStudentIds.length})`}
                                disabled={selectedStudentIds.length === 0}
                                style={{ flex: 1 }}
                            />
                        </View>
                    </View>
                </ModalM>
            )}
        </>
    )
}

const mapStateToProps = (state, ownProps) => {
    const studentId = ownProps?.student?.id
    return {
        chapters: state.data.chapters?.data,
        skillPartsByChapterId: state.data.chapters?.skillPartsByChapterId || {},
        getChaptersRequest: state.api.getChaptersAsTutor?.request,
        updateChapterRequest: state.api.updateMaterialChapter?.request,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        currentUser: state.data.currentUser?.object,
        isLiveMode: state.liveMode.isLiveModeEnabled,
        isParentMode: state.data.family?.isParentMode,
        liveSessionId: state.liveMode?.liveSessionId,
        studentAssessments: studentId
            ? state.data.schoolAssessments?.byStudentId?.[studentId] || []
            : [],
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(GenerateActivityModal)
